// ============================================================
// PUBLIC PAGES — Landing + marketing
//
// Auth pages (login, register, OTP, forgot, reset, verify) are no longer
// hosted here — auth.codivus.africa (the auth-api service) owns every auth
// screen. The platform redirects to it via OIDC; see ../sso.ts.
// ============================================================

import { state } from '../data.js';
import { navigate } from '../main.js';
import { toast } from '../main.js';

const LOGO_SVG = `<img src="/favicon.svg" width="28" height="28" alt="Codivus Code" />`;
const FOOTER_LOGO_SVG = `<svg width="28" height="28" viewBox="0 0 180 180" xmlns="http://www.w3.org/2000/svg"><path d="M0 90L45 45L45 75L30 90L45 105L45 135L0 90Z" fill="#f97316"/><rect x="75" y="30" width="30" height="120" fill="#2d3748" transform="rotate(-20 90 90)"/><path d="M180 90L135 45L135 75L150 90L135 105L135 135L180 90Z" fill="#f97316"/></svg>`;

function escHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Where the Codivus Code Web app lives. Configurable via window.__CODIVUS_WEB__
 * (e.g. set on a deployed `<script>` tag) so prod can point elsewhere if
 * needed. In dev, port 5174 (platform) infers 5173.
 */
export function webAppUrl(): string {
  const w = (typeof window !== 'undefined' && (window as any).__CODIVUS_WEB__) as string | undefined;
  if (typeof w === 'string' && w) return w.replace(/\/+$/, '');
  if (typeof location !== 'undefined' && location.hostname === 'localhost' && location.port === '5174') {
    return `${location.protocol}//${location.hostname}:5173`;
  }
  return 'https://web.codivus.africa';
}

/**
 * Where the Codivus Code Platform app lives — the authenticated dashboard hub.
 *
 * The public site has no signed-in UI of its own: the dashboard and the
 * entire OIDC sign-in flow live on the platform app. So "Sign in" / "Install"
 * hand off here, and the platform app redirects unauthenticated visitors to
 * auth.codivus.africa itself. Reads window.__CODIVUS_PLATFORM__ when a deploy
 * sets it (e.g. an injected config.js), otherwise the production default.
 */
function platformUrl(): string {
  const w = (typeof window !== 'undefined' && (window as any).__CODIVUS_PLATFORM__) as string | undefined;
  if (typeof w === 'string' && w) return w.replace(/\/+$/, '');
  return 'https://platform.codivus.africa';
}

/**
 * Where the management-api lives — used for cross-origin POSTs from the public
 * site (e.g. the enterprise sales-lead form). Deploy can override via the
 * injected window.__CODIVUS_MGMT_API__ global; falls back to the production
 * subdomain (see deploy/nginx/sites-available/platform.api.codivus.africa.conf).
 */
function managementApiUrl(): string {
  const w = (typeof window !== 'undefined' && (window as any).__CODIVUS_MGMT_API__) as string | undefined;
  if (typeof w === 'string' && w) return w.replace(/\/+$/, '');
  const api = (typeof window !== 'undefined' && (window as any).__CODIVUS_API__) as string | undefined;
  if (typeof api === 'string' && api) return api.replace(/\/+$/, '');
  // Local dev: server.js proxies /api/* to the local management-api, so
  // same-origin requests hit local data instead of dying on prod CORS
  // (which would leave pricing on the offline fallback — no MWK amounts).
  if (typeof location !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(location.hostname)) return '';
  return 'https://platform.api.codivus.africa';
}

// ---- Shared public shell with header ----

function publicShell(inner: string, active: 'home' | 'login' | 'register' | 'pricing' | ''): string {
  // For login/register, point href at the platform app (it owns sign-in);
  // for home, the in-app route. JS intercepts anyway via data-public-nav.
  const link = (key: 'home' | 'login' | 'register', label: string, path: string, cls = '') => {
    if (active === key) return '';
    const href = key === 'login' || key === 'register' ? platformUrl() : path;
    return `
    <a href="${href}" data-public-nav="${key}" class="${cls}">${label}</a>`;
  };
  const burgerIcon = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="4" y1="7"  x2="20" y2="7"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="17" x2="20" y2="17"/></svg>`;
  const isLight = typeof document !== 'undefined' && document.documentElement.dataset.theme === 'light';
  // Footer links that only toast "Coming soon" carry a visible badge so
  // visitors know before they click.
  const soonBadge = `<span class="badge gray footer-soon">Soon</span>`;
  return `
<div class="public-shell">
  <header class="public-header">
    <a class="public-brand" href="/" data-public-nav="home">
      <span class="public-brand-icon">${LOGO_SVG}</span>
      <span class="public-brand-text">
        <span class="public-brand-name">Codivus Code</span>
        <span class="public-brand-tag">Coding Partner</span>
      </span>
    </a>
    <nav class="public-nav" id="publicNav">
      <a href="/features" data-public-path="/features">Features</a>
      <a href="/install" data-public-path="/install">Install</a>
      <a href="/docs" data-public-path="/docs">Docs</a>
      <a href="/blog" data-public-path="/blog">Blog</a>
      <a href="/pricing" data-public-path="/pricing">Pricing</a>
      <a href="/enterprise" data-public-path="/enterprise">Enterprise</a>
      ${state.authenticated
        ? `<a href="${platformUrl()}" class="public-nav-cta">Dashboard</a>`
        : (active === 'login'
            ? link('register', 'Sign up', '/auth/register', 'public-nav-cta')
            : link('login', 'Login', '/auth/login', 'public-nav-cta'))}
    </nav>
    <button class="public-nav-icon" id="publicThemeBtn" aria-label="Toggle theme" title="Toggle theme">
      ${isLight
        ? `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>`
        : `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>`
      }
    </button>
    <button class="public-nav-toggle" id="publicNavToggle" aria-label="Menu" aria-expanded="false">${burgerIcon}</button>
  </header>
  <main class="public-main">${inner}</main>
  <footer class="public-footer">
    <div class="public-footer-inner">
      <div class="public-footer-brand">
        <div class="public-footer-logo">
          <span class="public-brand-icon">${FOOTER_LOGO_SVG}</span>
          <a href="https://codivus.africa" target="_blank" rel="noopener noreferrer" class="public-brand-name" style="text-decoration:none;color:inherit">Codivus Africa</a>
        </div>
        <p class="public-footer-tagline">A coding assistant that ships real engineering output.</p>
      </div>
      <div class="public-footer-cols">
        <div class="public-footer-col">
          <h4>Product</h4>
          <a href="${platformUrl()}" data-public-nav="register">Install</a>
          <a href="${platformUrl()}" data-public-nav="login">Sign in</a>
          <a href="/docs" data-public-path="/docs">Docs</a>
          <a href="#" id="publicChangelogBtn">Changelog${soonBadge}</a>
        </div>
        <div class="public-footer-col">
          <h4>Resources</h4>
          <a href="#" id="publicGuidesBtn">Guides${soonBadge}</a>
          <a href="#" id="publicApiBtn">API reference${soonBadge}</a>
          <a href="/blog" data-public-path="/blog">Blog</a>
          <a href="#" id="publicStatusBtn">Status${soonBadge}</a>
        </div>
        <div class="public-footer-col">
          <h4>Company</h4>
          <a href="#" id="publicAboutBtn">About${soonBadge}</a>
          <a href="/contact" data-public-path="/contact">Contact</a>
          <a href="/privacy" data-public-path="/privacy">Privacy</a>
          <a href="/terms" data-public-path="/terms">Terms</a>
        </div>
      </div>
    </div>
    <div class="public-footer-bottom">
      <span>&copy; ${new Date().getFullYear()} <a href="https://codivus.africa" target="_blank" rel="noopener noreferrer" style="color:inherit;text-decoration:none">Codivus Africa</a>. All rights reserved.</span>
      <!-- Social icons intentionally omitted: no public GitHub / X / LinkedIn
           profiles exist yet. Re-add each icon with its real profile URL when
           the account goes live. -->
    </div>
  </footer>
</div>`;
}

function bindPublicNav(): void {
  document.querySelectorAll<HTMLAnchorElement>('[data-public-nav]').forEach(a => {
    a.addEventListener('click', (e) => {
      e.preventDefault();
      const key = a.dataset.publicNav;

      // Collapse the mobile burger menu if it's open so tapping any header
      // link (including the logo) always closes the drawer.
      const navEl = document.getElementById('publicNav');
      const navToggle = document.getElementById('publicNavToggle');
      navEl?.classList.remove('open');
      navToggle?.setAttribute('aria-expanded', 'false');

      // The public site has no signed-in UI of its own — the dashboard and
      // the entire OIDC sign-in flow live on the platform app. "Sign in" and
      // "Install" hand off to platform.codivus.africa, which redirects to
      // auth.codivus.africa itself for unauthenticated visitors.
      if (key === 'login' || key === 'register') {
        location.href = platformUrl();
        return;
      }

      const path =
        key === 'home' ? '/' :
        key === 'pricing' ? '/pricing' :
        key === 'demos' ? '/demos' :
        '/';
      navigate(path);
    });
  });
  const currentPath = (location.pathname || '/').split('?')[0] || '/';
  document.querySelectorAll<HTMLAnchorElement>('[data-public-path]').forEach(a => {
    const target = a.dataset.publicPath || '/';
    if (target === currentPath) a.classList.add('active');
    a.addEventListener('click', (e) => {
      e.preventDefault();
      navigate(target);
    });
  });
  document.querySelectorAll<HTMLAnchorElement>('.public-footer a[href="#"]').forEach(a => {
    a.addEventListener('click', (e) => {
      e.preventDefault();
      toast('info', 'Coming soon', 'This page will land shortly.');
    });
  });
  const navToggle = document.getElementById('publicNavToggle');
  const navEl = document.getElementById('publicNav');
  navToggle?.addEventListener('click', () => {
    const open = navEl?.classList.toggle('open') ?? false;
    navToggle.setAttribute('aria-expanded', String(open));
  });
  document.getElementById('publicThemeBtn')?.addEventListener('click', () => {
    const cur = document.documentElement.dataset.theme === 'light' ? 'light' : 'dark';
    const next: 'light' | 'dark' = cur === 'light' ? 'dark' : 'light';
    document.documentElement.dataset.theme = next;
    try { localStorage.setItem('codivus-theme', next); } catch {}
    // Re-render the header so the icon flips
    const path = location.pathname || '/';
    navigate(path);
  });
}

// ---- Landing ----

export function renderLanding(): string {
  const inner = `
<section class="landing-split">
  <div class="landing-hero">
    <h1 class="landing-title">Ship your code faster</h1>
    <p class="landing-lede">Codivus Code coding assistant works across your codebase — planning, editing, running tools, and opening pull requests. Built for teams that want real engineering output, not just autocomplete.</p>
    <div class="landing-cta">
      <a class="btn btn-filled" href="/install" data-public-path="/install">Install</a>
      <a class="btn btn-ghost" href="/docs" data-public-path="/docs">Docs</a>
      <a class="btn btn-ghost" href="/demos" data-public-nav="demos">View Demos</a>
    </div>
  </div>

  <div class="agent-stage" aria-hidden="true">
    <div class="agent-stage-glow"></div>

    <!-- Orbit system -->
    <svg class="agent-orbit" viewBox="-40 -30 680 420" preserveAspectRatio="xMidYMid meet">
      <defs>
        <radialGradient id="coreGrad" cx="50%" cy="50%" r="50%">
          <stop offset="0%"  stop-color="#f97316" stop-opacity="1"/>
          <stop offset="60%" stop-color="#f97316" stop-opacity=".35"/>
          <stop offset="100%" stop-color="#f97316" stop-opacity="0"/>
        </radialGradient>
        <linearGradient id="linkGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stop-color="#f97316" stop-opacity="0"/>
          <stop offset="50%"  stop-color="#f97316" stop-opacity=".9"/>
          <stop offset="100%" stop-color="#f97316" stop-opacity="0"/>
        </linearGradient>
      </defs>

      <!-- Orbit rings -->
      <ellipse cx="300" cy="180" rx="240" ry="120" fill="none" stroke="var(--accent-ring)" stroke-width="1" stroke-dasharray="3 5" opacity=".6"/>
      <ellipse cx="300" cy="180" rx="170" ry="85"  fill="none" stroke="var(--accent-ring)" stroke-width="1" stroke-dasharray="2 4" opacity=".5"/>

      <!-- Core -->
      <circle cx="300" cy="180" r="62" fill="#f97316" class="agent-core"/>
      <circle cx="300" cy="180" r="62" fill="none" stroke="#fff" stroke-opacity=".45" stroke-width="1.5"/>
      <rect x="272" y="152" width="56" height="56" rx="12" ry="12" fill="#fff"/>
      <image class="core-logo" href="/favicon.svg" x="280" y="160" width="40" height="40"/>

      <!-- Orbiters: inner = solo human (slow), outer = human + codivus (fast) -->
      <g class="orbiter orbiter-solo">
        <!-- Background circle -->
        <circle r="14" fill="var(--bg-elevated)" stroke="var(--accent)" stroke-width="1.5"/>
        <!-- Single person -->
        <g fill="var(--accent)">
          <circle cy="-3.5" r="3.6"/>
          <path d="M-7,6 C-7,1 -3.2,-1.3 0,-1.3 C3.2,-1.3 7,1 7,6 Z"/>
        </g>
        <animateMotion dur="50s" repeatCount="indefinite"
          path="M470,180 A170,85 0 1,1 130,180 A170,85 0 1,1 470,180"/>
      </g>
      <g class="orbiter orbiter-pair">
        <g transform="translate(-15,0)">
          <circle r="13" fill="var(--bg-elevated)" stroke="var(--accent)" stroke-width="1.5"/>
          <circle cx="0" cy="-4" r="3.8" fill="var(--accent)"/>
          <path d="M-8,6.5 C-8,1 -4,-1.5 0,-1.5 C4,-1.5 8,1 8,6.5 Z" fill="var(--accent)"/>
        </g>
        <g transform="translate(15,0)" class="orbiter-codivus">
          <circle r="13" fill="var(--bg-elevated)" stroke="var(--accent)" stroke-width="1.5"/>
          <image href="/favicon.svg" x="-9" y="-9" width="18" height="18"/>
        </g>
        <animateMotion dur="7s" repeatCount="indefinite"
          path="M540,180 A240,120 0 1,1 60,180 A240,120 0 1,1 540,180"/>
      </g>

    </svg>

    <!-- Workbench: terminal + diff + pipeline -->
    <div class="agent-panels">
      <div class="agent-panel agent-terminal">
        <div class="agent-panel-chrome">
          <span class="dot r"></span><span class="dot y"></span><span class="dot g"></span>
          <span class="agent-panel-title">Codivus Code · session #4a2f</span>
          <span class="agent-live"><span class="pulse"></span>live</span>
        </div>
        <div class="agent-terminal-body">
          <div class="log-track">
            <div class="log"><span class="t">0.12s</span><span class="a">read</span> src/routes/auth.ts</div>
            <div class="log"><span class="t">0.48s</span><span class="a">plan</span> add /logout endpoint, wire middleware</div>
            <div class="log"><span class="t">0.91s</span><span class="a">edit</span> auth.ts <span class="plus">+18</span> <span class="minus">-4</span></div>
            <div class="log"><span class="t">1.24s</span><span class="a">edit</span> middleware.ts <span class="plus">+6</span></div>
            <div class="log"><span class="t">1.88s</span><span class="a">run</span> pnpm test <span class="ok">\u{2713} 142/142</span></div>
            <div class="log"><span class="t">2.41s</span><span class="a">commit</span> feat(auth): add logout route</div>
            <div class="log"><span class="t">3.02s</span><span class="a">push</span> origin/agent/logout-endpoint</div>
            <div class="log"><span class="t">3.55s</span><span class="a">pr</span> opened #142 <span class="ok">ready for review</span></div>
            <!-- duplicate for seamless scroll -->
            <div class="log"><span class="t">0.12s</span><span class="a">read</span> src/routes/auth.ts</div>
            <div class="log"><span class="t">0.48s</span><span class="a">plan</span> add /logout endpoint, wire middleware</div>
            <div class="log"><span class="t">0.91s</span><span class="a">edit</span> auth.ts <span class="plus">+18</span> <span class="minus">-4</span></div>
            <div class="log"><span class="t">1.24s</span><span class="a">edit</span> middleware.ts <span class="plus">+6</span></div>
          </div>
          <div class="agent-caret">_</div>
        </div>
      </div>

      <div class="agent-panel agent-diff">
        <div class="agent-panel-chrome">
          <span class="dot r"></span><span class="dot y"></span><span class="dot g"></span>
          <span class="agent-panel-title">auth.ts · diff</span>
        </div>
        <pre class="agent-diff-body"><span class="line ctx"> export function authRoutes(app) {</span>
<span class="line ctx">   app.post('/login', login);</span>
<span class="line minus">-  app.post('/logout', legacy);</span>
<span class="line plus">+  app.post('/logout', requireAuth, logout);</span>
<span class="line plus">+  app.get('/session', requireAuth, session);</span>
<span class="line ctx"> }</span></pre>
      </div>

      <div class="agent-panel agent-pipe">
        <div class="agent-panel-chrome">
          <span class="dot r"></span><span class="dot y"></span><span class="dot g"></span>
          <span class="agent-panel-title">pipeline</span>
        </div>
        <div class="agent-pipe-body">
          <div class="step s1"><span class="step-dot"></span><span class="step-label">Read repo</span></div>
          <div class="step s2"><span class="step-dot"></span><span class="step-label">Plan changes</span></div>
          <div class="step s3"><span class="step-dot"></span><span class="step-label">Edit files</span></div>
          <div class="step s4"><span class="step-dot"></span><span class="step-label">Run tests</span></div>
          <div class="step s5"><span class="step-dot"></span><span class="step-label">Open PR</span></div>
        </div>
      </div>
    </div>
  </div>

  <div class="capabilities-section">
    <div class="capabilities-header">
      <h2 class="capabilities-title">Capabilities</h2>
      <p class="capabilities-subtitle">What Codivus Code can do end-to-end, without leaving your editor.</p>
    </div>
    <div class="capabilities-bento">

      <!-- 1. Reads codebase (wide, with animated file tree) -->
      <article class="bento bento-wide cap-read">
        <div class="bento-art">
          <div class="mini-tree">
            <div class="mt-row mt-root"><span class="mt-caret">\u25BE</span> src</div>
            <div class="mt-row mt-nested"><span class="mt-dot"></span> routes/</div>
            <div class="mt-row mt-nested mt-active"><span class="mt-dot mt-dot-on"></span> auth.ts</div>
            <div class="mt-row mt-nested"><span class="mt-dot"></span> middleware.ts</div>
            <div class="mt-row mt-root"><span class="mt-caret">\u25B8</span> tests</div>
            <div class="mt-scan"></div>
          </div>
        </div>
        <div class="bento-body">
          <h3>Reads your codebase</h3>
          <p>Navigates files, symbols, git history, and dependencies — builds context before it changes a line.</p>
        </div>
      </article>

      <!-- 2. Image understanding -->
      <article class="bento cap-image">
        <div class="bento-art">
          <div class="mini-image">
            <svg viewBox="0 0 120 80" xmlns="http://www.w3.org/2000/svg">
              <rect x="2" y="2" width="116" height="76" rx="8" fill="var(--bg-overlay)" stroke="var(--accent-ring)"/>
              <circle cx="30" cy="28" r="8" fill="#fbbf24"/>
              <path d="M2 62 L40 40 L68 55 L96 32 L118 48 L118 78 L2 78 Z" fill="var(--accent)" opacity=".55"/>
              <path d="M2 62 L40 40 L68 55 L96 32 L118 48" stroke="var(--accent)" stroke-width="1.5" fill="none"/>
            </svg>
            <div class="mi-scan"></div>
          </div>
        </div>
        <div class="bento-body">
          <h3>Image understanding</h3>
          <p>Reads screenshots, mockups, and diagrams as input.</p>
        </div>
      </article>

      <!-- 3. Edits code (live mini diff) -->
      <article class="bento cap-edit">
        <div class="bento-art">
          <pre class="mini-diff"><span class="md-ctx">  if (user) {</span>
<span class="md-minus">-   return oldAuth(user);</span>
<span class="md-plus">+   return verify(user);</span>
<span class="md-plus">+   audit.log(user.id);</span>
<span class="md-ctx">  }</span></pre>
        </div>
        <div class="bento-body">
          <h3>Edits code</h3>
          <p>Multi-file diffs with precise, reviewable changes.</p>
        </div>
      </article>

      <!-- 4. Runs tests (progress ring + check) -->
      <article class="bento cap-tests">
        <div class="bento-art">
          <svg class="mini-ring" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg">
            <circle cx="40" cy="40" r="32" fill="none" stroke="var(--accent-soft)" stroke-width="6"/>
            <circle class="mini-ring-fill" cx="40" cy="40" r="32" fill="none" stroke="var(--accent)" stroke-width="6"
                    stroke-linecap="round" stroke-dasharray="201" stroke-dashoffset="0" transform="rotate(-90 40 40)"/>
            <path d="M26 41 L36 51 L55 31" fill="none" stroke="var(--accent)" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <div class="mini-count"><span>142</span>/142 passing</div>
        </div>
        <div class="bento-body">
          <h3>Runs &amp; fixes tests</h3>
          <p>Executes suites, reads failures, iterates until green.</p>
        </div>
      </article>

      <!-- 5. Tools & commands (terminal) -->
      <article class="bento cap-tools">
        <div class="bento-art">
          <div class="mini-term">
            <div class="mt-chrome"><span class="mt-dot-r"></span><span class="mt-dot-y"></span><span class="mt-dot-g"></span></div>
            <div class="mt-line"><span class="mt-prompt">$</span> pnpm install</div>
            <div class="mt-line"><span class="mt-prompt">$</span> pnpm lint <span class="mt-ok">\u{2713}</span></div>
            <div class="mt-line"><span class="mt-prompt">$</span> pnpm build<span class="mt-caret-blink">_</span></div>
          </div>
        </div>
        <div class="bento-body">
          <h3>Runs tools &amp; commands</h3>
          <p>Shell, package managers, linters, build scripts.</p>
        </div>
      </article>

      <!-- 6. Search & debug (magnifier over log) -->
      <!-- Opens PRs (3/4 width, git branches) — pairs with cap-search (1/4) on the same row. -->
      <article class="bento bento-three-quarter cap-pr">
        <div class="bento-art">
          <svg class="mini-branch" viewBox="0 0 260 120" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 60 H80 Q100 60 100 40 V30" stroke="var(--accent)" stroke-width="3" fill="none" stroke-linecap="round"/>
            <path d="M20 60 H80 Q100 60 100 80 V90" stroke="var(--accent-ring)" stroke-width="3" fill="none" stroke-linecap="round"/>
            <path d="M100 30 H180 Q200 30 200 60 H240" stroke="var(--accent)" stroke-width="3" fill="none" stroke-linecap="round"/>
            <path d="M100 90 H180 Q200 90 200 60" stroke="var(--accent-ring)" stroke-width="3" fill="none" stroke-linecap="round"/>
            <circle cx="20"  cy="60" r="6" fill="var(--accent)"/>
            <circle cx="100" cy="30" r="6" fill="var(--accent)"/>
            <circle cx="100" cy="90" r="6" fill="var(--accent-ring)"/>
            <circle cx="240" cy="60" r="10" fill="var(--accent)"/>
            <text x="240" y="64" text-anchor="middle" font-size="10" font-weight="700" fill="#fff">\u2713</text>
            <text x="14"  y="52" font-size="10" font-family="var(--font-mono)" fill="var(--text-3)">main</text>
            <text x="94"  y="22" font-size="10" font-family="var(--font-mono)" fill="var(--text-2)">agent/logout</text>
            <text x="206" y="54" font-size="10" font-family="var(--font-mono)" fill="var(--text-1)" font-weight="700">PR #142</text>
          </svg>
        </div>
        <div class="bento-body">
          <h3>Opens pull requests</h3>
          <p>Commits, pushes, and drafts PR titles and bodies — ready for review.</p>
        </div>
      </article>

      <!-- Searches & debugs — fills the remaining quarter of the PR row. -->
      <article class="bento bento-quarter cap-search">
        <div class="bento-art">
          <svg class="mini-search" viewBox="0 0 120 80" xmlns="http://www.w3.org/2000/svg">
            <rect x="6" y="14" width="88" height="4" rx="2" fill="var(--accent-soft)"/>
            <rect x="6" y="26" width="72" height="4" rx="2" fill="var(--accent-soft)"/>
            <rect x="6" y="38" width="96" height="4" rx="2" fill="var(--accent)" opacity=".6"/>
            <rect x="6" y="50" width="60" height="4" rx="2" fill="var(--accent-soft)"/>
            <rect x="6" y="62" width="80" height="4" rx="2" fill="var(--accent-soft)"/>
            <g class="mini-search-glass">
              <circle cx="78" cy="44" r="16" fill="none" stroke="var(--accent)" stroke-width="3"/>
              <line x1="90" y1="56" x2="104" y2="70" stroke="var(--accent)" stroke-width="3" stroke-linecap="round"/>
            </g>
          </svg>
        </div>
        <div class="bento-body">
          <h3>Searches &amp; debugs</h3>
          <p>Traces bugs through stacks, logs, and live output.</p>
        </div>
      </article>

      <!-- Chat -->
      <article class="bento cap-chat">
        <div class="bento-art">
          <div class="mini-chat">
            <div class="mc-bubble mc-user">Add /logout route</div>
            <div class="mc-bubble mc-agent">
              <span class="mc-typing"><span></span><span></span><span></span></span>
            </div>
          </div>
        </div>
        <div class="bento-body">
          <h3>Chats in plain English</h3>
          <p>Clarifies scope, explains tradeoffs, pair-programs.</p>
        </div>
      </article>

      <!-- 9. Audit -->
      <article class="bento cap-audit">
        <div class="bento-art">
          <svg class="mini-shield" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg">
            <path d="M40 6 L68 16 V38 Q68 62 40 74 Q12 62 12 38 V16 Z" fill="var(--accent-soft)" stroke="var(--accent)" stroke-width="2"/>
            <path d="M28 40 L37 49 L54 32" fill="none" stroke="var(--accent)" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="bento-body">
          <h3>Audit &amp; controls</h3>
          <p>Every tool call logged. Scoped keys and permissions.</p>
        </div>
      </article>

      <!-- 10. Writes documentation — fills the third cell of the final row. -->
      <article class="bento cap-docs">
        <div class="bento-art">
          <svg class="mini-docs" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg">
            <rect x="18" y="10" width="44" height="58" rx="4" fill="var(--accent-soft)" stroke="var(--accent)" stroke-width="2"/>
            <path d="M50 10 V22 H62" fill="none" stroke="var(--accent)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <line x1="26" y1="32" x2="54" y2="32" stroke="var(--accent)" stroke-width="2" stroke-linecap="round" opacity=".75"/>
            <line x1="26" y1="40" x2="54" y2="40" stroke="var(--accent)" stroke-width="2" stroke-linecap="round" opacity=".55"/>
            <line x1="26" y1="48" x2="48" y2="48" stroke="var(--accent)" stroke-width="2" stroke-linecap="round" opacity=".55"/>
            <line x1="26" y1="56" x2="42" y2="56" stroke="var(--accent)" stroke-width="2" stroke-linecap="round" opacity=".55"/>
          </svg>
        </div>
        <div class="bento-body">
          <h3>Writes documentation</h3>
          <p>Generates JSDoc, docstrings, and READMEs from the actual code.</p>
        </div>
      </article>

    </div>
  </div>

  <div class="why-section">
    <div class="why-header">
      <h2 class="why-title">Why teams choose Codivus Code</h2>
      <p class="why-subtitle">Real engineering output, not a smarter autocomplete — with the controls teams actually need.</p>
    </div>
    <div class="why-grid">
      <article class="why-card">
        <div class="why-num">01</div>
        <h3>Plan first</h3>
        <p>Plans, edits, runs tests, and opens a pull request. The agent finishes tasks — it doesn't stop halfway with a suggestion.</p>
      </article>
      <article class="why-card">
        <div class="why-num">02</div>
        <h3>No Hallucination</h3>
        <p>Codivus Code runs your actual test suite, reads failures, and iterates. If it says the change works, the suite proves it.</p>
      </article>
      <article class="why-card">
        <div class="why-num">03</div>
        <h3>Strictly follows your tech stack</h3>
        <p>Works with pnpm, yarn, turbo, nx, cargo, pytest, go — whatever you already have. No framework rewrites, no lock-in.</p>
      </article>
      <article class="why-card">
        <div class="why-num">04</div>
        <h3>Secure by default</h3>
        <p>Scoped API tokens, per-project permissions, and a full audit log of every tool call the agent makes.</p>
      </article>
      <article class="why-card">
        <div class="why-num">05</div>
        <h3>Saves costs</h3>
        <p>Credit-based billing pegged to real compute cost — aggressive prompt caching and smart context trimming mean you pay for work, not waste.</p>
      </article>
      <article class="why-card">
        <div class="why-num">06</div>
        <h3>Local currency support</h3>
        <p>Billed in what makes sense for you — USD or MWK — with pricing that reflects local purchasing power, not just exchange rates.</p>
      </article>
    </div>
  </div>
</section>`;
  return publicShell(inner, 'home');
}

export function initLanding(): void {
  bindPublicNav();
}

// ---- Pricing ----

type PricingCurrency = 'usd' | 'mwk';

interface PricingPlan {
  id: string;
  name: string;
  // Price + cadence per supported currency. Amounts mirror the plans set in
  // the platform admin (management-api `plans` table) and are refreshed live
  // by fetchLivePricingPlans(); these are the offline fallback.
  prices: Record<PricingCurrency, { amount: string; cadence: string }>;
  blurb: string;
  cta: string;
  highlighted?: boolean;
  features: string[];
}

const PRICING_PLANS: PricingPlan[] = [
  {
    id: 'free',
    name: 'Free',
    prices: {
      usd: { amount: '$0',   cadence: 'forever' },
      mwk: { amount: 'MK 0', cadence: 'forever' },
    },
    blurb: 'Try Codivus Code on personal projects.',
    cta: 'Get started',
    features: [
      '1,000,000 credits / month',
      '3 projects / month',
      'CLI + VS Code extension',
      'Community support',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    prices: {
      usd: { amount: '$10', cadence: 'per user / month' },
      // MWK is FX-derived from the live USD price; offline there is no rate.
      mwk: { amount: '—',   cadence: 'per user / month' },
    },
    blurb: 'Everything a solo engineer needs.',
    cta: 'Upgrade to Pro',
    highlighted: true,
    features: [
      '5,000,000 credits / month',
      'Unlimited projects',
      'Parallel sessions',
      'Top up with credit packs',
      'Email support',
    ],
  },
  {
    id: 'team',
    name: 'Team',
    prices: {
      usd: { amount: '$25', cadence: 'per user / month' },
      mwk: { amount: '—',   cadence: 'per user / month' },
    },
    blurb: 'For teams that ship together.',
    cta: 'Upgrade to Team',
    features: [
      'Everything in Pro',
      '20,000,000 credits / month',
      'Shared workspaces',
      'SSO (Google, Okta)',
      'Audit logs & role controls',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    prices: {
      usd: { amount: 'Custom', cadence: 'talk to sales' },
      mwk: { amount: 'Custom', cadence: 'talk to sales' },
    },
    blurb: 'Custom contracts, dedicated support.',
    cta: 'Contact sales',
    features: [
      'Custom credit volume',
      'Self-hosted or VPC',
      'SOC 2 & custom DPAs',
      'Dedicated success engineer',
      '99.9% SLA',
    ],
  },
];

// Live prices come from the management API and overlay the fallback amounts in
// PRICING_PLANS. Blurbs, CTAs, and feature bullets stay local — they're
// marketing copy, not billing data. Mirrors apps/web PricingView.vue.
let pricingPlans: PricingPlan[] = PRICING_PLANS;

// `price` arrives in minor units (cents for USD, tambala for MWK).
function formatPricingAmount(currency: PricingCurrency, minorUnits: number): string {
  const num = Math.floor(minorUnits / 100).toLocaleString();
  return currency === 'mwk' ? `MK ${num}` : `$${num}`;
}

// `limits.tokensPerMonth` holds the plan's monthly CREDIT allocation — the
// field name predates the credit migration (see docs/credits-system.md).
interface ApiPlan {
  id: string;
  name: string;
  price: number;
  currency: string;
  limits?: Record<string, number | string[]>;
}

// Overwrite the "N credits / month" bullet with the live allocation so the
// marketing copy can't drift from what billing actually enforces.
function withLiveCredits(features: string[], plan: ApiPlan | undefined): string[] {
  const credits = plan?.limits?.tokensPerMonth;
  if (typeof credits !== 'number') return features;
  const bullet = credits < 0 ? 'Unlimited credits' : `${credits.toLocaleString()} credits / month`;
  return features.map(f => /credits \/ month$/.test(f) ? bullet : f);
}

async function fetchLivePricingPlans(): Promise<PricingPlan[] | null> {
  try {
    const res = await fetch(`${managementApiUrl()}/api/subscription/plans?product=codivus`);
    if (!res.ok) return null;
    const data = await res.json() as { plans?: ApiPlan[]; fx?: { mwkPerUsd?: number } };
    const apiPlans = data.plans ?? [];
    if (!apiPlans.length) return null;
    // Catalogue prices are USD-only; the MWK amount is the USD price converted
    // at the server's public pricing rate (`fx.mwkPerUsd`) — the same rate
    // checkout charges at, so the page never disagrees with the bill.
    const mwkPerUsd = data.fx?.mwkPerUsd ?? 0;

    return PRICING_PLANS.map((fallback) => {
      if (fallback.id === 'enterprise') return fallback; // always "Custom"
      const plan = apiPlans.find(p => p.id === fallback.id);
      if (!plan) return fallback;
      const features = withLiveCredits(fallback.features, plan);
      if (plan.price <= 0) {
        // Free tier — $0 / MK 0 "forever" copy stays as-is.
        return { ...fallback, name: plan.name, features };
      }
      return {
        ...fallback,
        name: plan.name,
        features,
        prices: {
          usd: { amount: formatPricingAmount('usd', plan.price), cadence: 'per user / month' },
          mwk: {
            amount: mwkPerUsd > 0 ? formatPricingAmount('mwk', Math.round(plan.price * mwkPerUsd)) : '—',
            cadence: 'per user / month',
          },
        },
      };
    });
  } catch {
    return null; // offline / API down — keep fallback prices
  }
}

function detectDefaultPricingCurrency(): PricingCurrency {
  // Best-effort: visitors from MW (Malawi) default to MWK; everyone else USD.
  // Reads either the user's locale or a previously-stored preference.
  try {
    const stored = localStorage.getItem('codivus.pricing.currency') as PricingCurrency | null;
    if (stored === 'usd' || stored === 'mwk') return stored;
    const region = (Intl.DateTimeFormat().resolvedOptions().locale || '').toUpperCase();
    if (region.includes('-MW') || region.endsWith('MW')) return 'mwk';
  } catch { /* localStorage / Intl unavailable — fall through */ }
  return 'usd';
}

// Resolve the CTA href for a plan. Free → platform root (sign-up/dashboard).
// Pro/Team → deep-link into the platform billing page with
// ?plan=<id>&currency=<usd|mwk>&product=codivus, which starts checkout for
// that plan directly. There is one plan id per tier (USD catalogue price);
// the currency param tells checkout to FX-convert to MWK at the public rate.
// Enterprise returns '#' (click handler short-circuits to a "contact sales" toast).
function pricingCtaHref(p: PricingPlan, currency: PricingCurrency): string {
  if (p.id === 'enterprise') return '#';
  if (p.id === 'free') return platformUrl();
  return `${platformUrl()}/billing?plan=${encodeURIComponent(p.id)}&currency=${currency}&product=codivus`;
}

function renderPricingGrid(currency: PricingCurrency): string {
  const check = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`;
  const card = (p: PricingPlan) => {
    const price = p.prices[currency];
    return `
    <article class="price-card${p.highlighted ? ' price-card-featured' : ''}">
      ${p.highlighted ? '<div class="price-ribbon">Most popular</div>' : ''}
      <div class="price-name">${p.name}</div>
      <div class="price-amount">
        <span class="price-num">${price.amount}</span>
        <span class="price-cadence">${price.cadence}</span>
      </div>
      <p class="price-blurb">${p.blurb}</p>
      <a class="btn ${p.highlighted ? 'btn-filled' : 'btn-ghost'} price-cta" href="${pricingCtaHref(p, currency)}" data-pricing-cta="${p.id}">${p.cta}</a>
      <ul class="price-features">
        ${p.features.map(f => `<li><span class="price-check">${check}</span>${f}</li>`).join('')}
      </ul>
    </article>`;
  };
  return pricingPlans.map(card).join('');
}

// Current selection = the active tab if the page is mounted, else the default.
function currentPricingCurrency(): PricingCurrency {
  const active = document.querySelector<HTMLButtonElement>('[data-pricing-currency].active');
  const c = active?.dataset.pricingCurrency;
  return c === 'usd' || c === 'mwk' ? c : detectDefaultPricingCurrency();
}

export function renderPricing(): string {
  const currency = detectDefaultPricingCurrency();
  const inner = `
<section class="pricing-section">
  <div class="pricing-header">
    <h1 class="pricing-title">Pricing</h1>
    <p class="pricing-subtitle">Pick a plan that fits how your team ships. Cancel anytime.</p>
    <div class="pricing-currency-tabs" role="tablist" aria-label="Display currency">
      <button class="pricing-currency-tab${currency === 'usd' ? ' active' : ''}" role="tab" aria-selected="${currency === 'usd'}" data-pricing-currency="usd">USD</button>
      <button class="pricing-currency-tab${currency === 'mwk' ? ' active' : ''}" role="tab" aria-selected="${currency === 'mwk'}" data-pricing-currency="mwk">MWK</button>
    </div>
  </div>
  <div class="pricing-grid" id="pricingGrid">
    ${renderPricingGrid(currency)}
  </div>
  <div class="pricing-faq">
    <div class="faq-item">
      <h4>What is a credit?</h4>
      <p>Credits are how usage is measured. Every model call is billed by its actual compute cost — cheaper models and cached context use fewer credits, so you only pay for what the agent really consumes.</p>
    </div>
    <div class="faq-item">
      <h4>What happens when I run out of credits?</h4>
      <p>Pro and Team can top up anytime with one-off credit packs — no plan change needed. Your monthly allocation renews with each billing period.</p>
    </div>
    <div class="faq-item">
      <h4>Do credits roll over?</h4>
      <p>Plan credits reset each billing period. Purchased credit packs keep their balance until used or expired.</p>
    </div>
    <div class="faq-item">
      <h4>How does billing work?</h4>
      <p>Monthly per seat, with a fresh credit allocation each period. Upgrade, downgrade, or cancel from your workspace.</p>
    </div>
  </div>
</section>
${renderSalesModal()}`;
  return publicShell(inner, 'pricing');
}

// Enterprise contact form. "Coming soon" products are listed but disabled —
// they can't be selected or submitted until they launch. Server-side
// validation is the source of truth; this list only drives the dropdown.
interface SalesProductOption { id: string; name: string; comingSoon?: boolean }
const SALES_PRODUCT_OPTIONS: SalesProductOption[] = [
  { id: 'codivus',          name: 'Codivus Code (coding agent)' },
  { id: 'text-to-diagrams', name: 'Codivus Diagrams' },
  { id: 'cobot',            name: 'Codivus Cobot',  comingSoon: true },
  { id: 'dreamjobs',        name: 'DreamJobs',      comingSoon: true },
];

function renderSalesModal(): string {
  const options = SALES_PRODUCT_OPTIONS
    .map((p) => `<option value="${p.id}"${p.comingSoon ? ' disabled' : ''}>${p.name}${p.comingSoon ? ' (coming soon)' : ''}</option>`)
    .join('');
  return `
<div class="doc-modal" id="salesModal" role="dialog" aria-modal="true" aria-labelledby="salesModalTitle">
  <div class="doc-modal-backdrop" data-sales-close></div>
  <div class="doc-modal-panel" style="max-width:480px;padding:32px 36px 28px">
    <button class="doc-modal-close" aria-label="Close" data-sales-close>&times;</button>
    <h2 id="salesModalTitle" style="margin:0 0 6px;font-size:20px">Enterprise subscription request</h2>
    <p style="margin:0 0 20px;color:var(--text-2);font-size:14px">Tell us which product you're interested in and we'll be in touch within a business day.</p>
    <form id="salesForm" novalidate>
      <label style="display:block;font-size:13px;font-weight:600;margin-bottom:6px" for="salesEmail">Work email</label>
      <input id="salesEmail" name="email" type="email" required autocomplete="email"
        style="width:100%;padding:10px 12px;border:1px solid var(--border);border-radius:8px;background:var(--bg-1);color:var(--text-1);font-size:14px;margin-bottom:16px"
        placeholder="you@company.com" />

      <label style="display:block;font-size:13px;font-weight:600;margin-bottom:6px" for="salesProduct">Product</label>
      <select id="salesProduct" name="productId" required
        style="width:100%;padding:10px 12px;border:1px solid var(--border);border-radius:8px;background:var(--bg-1);color:var(--text-1);font-size:14px;margin-bottom:16px">
        <option value="" disabled selected>Select a product…</option>
        ${options}
      </select>

      <label style="display:block;font-size:13px;font-weight:600;margin-bottom:6px" for="salesMessage">Anything to add? <span style="font-weight:400;color:var(--text-3)">(optional)</span></label>
      <textarea id="salesMessage" name="message" rows="3" maxlength="4000"
        style="width:100%;padding:10px 12px;border:1px solid var(--border);border-radius:8px;background:var(--bg-1);color:var(--text-1);font-size:14px;margin-bottom:20px;resize:vertical;font-family:inherit"
        placeholder="Team size, deployment model, timeline…"></textarea>

      <div style="display:flex;gap:8px;justify-content:flex-end">
        <button type="button" class="btn btn-ghost" data-sales-close>Cancel</button>
        <button type="submit" class="btn btn-filled" id="salesSubmit">Send request</button>
      </div>
    </form>
  </div>
</div>`;
}

function openSalesModal(): void {
  const m = document.getElementById('salesModal');
  if (!m) return;
  m.classList.add('open');
  const email = document.getElementById('salesEmail') as HTMLInputElement | null;
  email?.focus();
}

function closeSalesModal(): void {
  const m = document.getElementById('salesModal');
  if (!m) return;
  m.classList.remove('open');
  const form = document.getElementById('salesForm') as HTMLFormElement | null;
  form?.reset();
}

async function submitSalesLead(payload: { email: string; productId: string; message: string }): Promise<void> {
  const res = await fetch(`${managementApiUrl()}/api/sales-leads`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let msg = 'Request failed. Please try again.';
    try { const j = await res.json(); if (j?.error) msg = j.error; } catch { /* non-json */ }
    throw new Error(msg);
  }
}

function bindSalesModal(): void {
  document.querySelectorAll<HTMLElement>('[data-sales-close]').forEach((el) => {
    el.addEventListener('click', closeSalesModal);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && document.getElementById('salesModal')?.classList.contains('open')) {
      closeSalesModal();
    }
  });
  const form = document.getElementById('salesForm') as HTMLFormElement | null;
  const btn = document.getElementById('salesSubmit') as HTMLButtonElement | null;
  form?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const email = (document.getElementById('salesEmail') as HTMLInputElement | null)?.value.trim() ?? '';
    const productId = (document.getElementById('salesProduct') as HTMLSelectElement | null)?.value ?? '';
    const message = (document.getElementById('salesMessage') as HTMLTextAreaElement | null)?.value.trim() ?? '';
    if (!email || !productId) {
      toast('error', 'Missing fields', 'Email and product are required.');
      return;
    }
    if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
    try {
      await submitSalesLead({ email, productId, message });
      toast('success', 'Request sent', 'Our team will reach out shortly.');
      closeSalesModal();
    } catch (err) {
      toast('error', 'Could not send', err instanceof Error ? err.message : 'Please try again.');
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = 'Send request'; }
    }
  });
}

function bindPricingCtas(): void {
  document.querySelectorAll<HTMLAnchorElement>('[data-pricing-cta]').forEach(a => {
    a.addEventListener('click', (e) => {
      const id = a.dataset.pricingCta;
      if (id === 'enterprise') {
        e.preventDefault();
        openSalesModal();
        return;
      }
      // Other plans link to an internal route — intercept so it's an SPA
      // navigation instead of a full page load.
      const href = a.getAttribute('href');
      if (href && href.startsWith('/')) {
        e.preventDefault();
        navigate(href);
      }
    });
  });
}

export function initPricing(): void {
  bindPublicNav();
  bindPricingCtas();
  bindSalesModal();
  const grid = document.getElementById('pricingGrid');
  // Refresh prices from the management API; the page first paints with the
  // last-known (or fallback) amounts, then swaps in live ones.
  void fetchLivePricingPlans().then((live) => {
    if (!live) return;
    pricingPlans = live;
    const g = document.getElementById('pricingGrid');
    if (g) {
      g.innerHTML = renderPricingGrid(currentPricingCurrency());
      bindPricingCtas();
    }
  });
  document.querySelectorAll<HTMLButtonElement>('[data-pricing-currency]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const currency = btn.dataset.pricingCurrency as PricingCurrency;
      if (currency !== 'usd' && currency !== 'mwk') return;
      try { localStorage.setItem('codivus.pricing.currency', currency); } catch { /* private mode */ }
      document.querySelectorAll<HTMLButtonElement>('[data-pricing-currency]').forEach((b) => {
        const active = b.dataset.pricingCurrency === currency;
        b.classList.toggle('active', active);
        b.setAttribute('aria-selected', String(active));
      });
      if (grid) {
        grid.innerHTML = renderPricingGrid(currency);
        bindPricingCtas();
      }
    });
  });
}

// ---- Features ----

export function renderFeatures(): string {
  const items = [
    { icon: '\u{1F9E0}', title: 'Reads your codebase',     body: 'Navigates files, symbols, git history, and dependencies — builds real context before changing a line.' },
    { icon: '\u{270F}\u{FE0F}', title: 'Edits code across files', body: 'Precise multi-file diffs. Every change is reviewable, revertible, and attributed to the agent session.' },
    { icon: '\u{2705}', title: 'Runs & fixes tests',        body: 'Executes your suite, parses failures, and iterates until it\'s green. No hallucinated passes.' },
    { icon: '\u{1F527}', title: 'Runs tools & commands',    body: 'Shell, package managers, linters, build scripts — scoped to the project and auditable.' },
    { icon: '\u{1F50D}', title: 'Searches & debugs',        body: 'Traces bugs through stacks, logs, and live output. Finds root causes instead of masking symptoms.' },
    { icon: '\u{1F680}', title: 'Opens pull requests',      body: 'Commits to an agent branch, pushes, and drafts a PR with a clean title and body for your team to review.' },
    { icon: '\u{1F4AC}', title: 'Chats in plain English',   body: 'Clarifies scope, explains trade-offs, and pair-programs. Knows when to stop and ask.' },
    { icon: '\u{1F5BC}\u{FE0F}', title: 'Image understanding', body: 'Drop in screenshots, mockups, or diagrams — Codivus Code reads them as part of the task context.' },
    { icon: '\u{1F510}', title: 'Audit & controls',         body: 'Every tool call logged. Scoped API keys, role-based permissions, and per-project limits.' },
  ];
  const tile = (i: typeof items[0]) => `
    <article class="feature-card">
      <span class="feature-icon">${i.icon}</span>
      <h3>${i.title}</h3>
      <p>${i.body}</p>
    </article>`;
  const inner = `
<section class="generic-section">
  <header class="generic-header">
    <h1>Features</h1>
    <p>Codivus Code isn't a chat widget bolted onto your editor. It's a full engineering loop — planning, editing, running, and shipping.</p>
  </header>
  <div class="feature-grid">
    ${items.map(tile).join('')}
  </div>
  <div class="generic-cta">
    <a class="btn btn-filled" href="${platformUrl()}" data-public-nav="register">Start building free</a>
    <a class="btn btn-ghost" href="/pricing" data-public-path="/pricing">See pricing</a>
  </div>
</section>`;
  return publicShell(inner, '');
}

export function initFeatures(): void { bindPublicNav(); }

// ---- Docs ----

type DocArticle = { title: string; section: string; read: string; body: string };
const DOC_ARTICLES: Record<string, DocArticle> = {
  'Install the CLI': { title: 'Install the CLI', section: 'Getting started', read: '2 min read', body: `
    <p>The Codivus Code CLI is the fastest way to run the agent against any local repository. It ships as a single <code>codivus</code> binary.</p>
    <h3>Install with npm</h3>
    <pre><code>npm install -g @codivus/cli</code></pre>
    <h3>Verify the install</h3>
    <pre><code>codivus --version
# codivus 1.4.2</code></pre>
    <h3>Updating</h3>
    <p>Run <code>codivus update</code> or re-run your package manager's upgrade command. The CLI will prompt when a new version is available.</p>`},
  'Quickstart: your first task': { title: 'Quickstart: your first task', section: 'Getting started', read: '4 min read', body: `
    <p>This guide walks you through your first Codivus Code run — from clean repo to an opened pull request — in under five minutes.</p>
    <h3>1. Point Codivus Code at your repo</h3>
    <pre><code>cd my-project
codivus init</code></pre>
    <h3>2. Give it a task</h3>
    <pre><code>codivus "add a /logout endpoint that revokes the session"</code></pre>
    <h3>3. Review the plan</h3>
    <p>Codivus Code will print its plan and ask for approval. Use <kbd>y</kbd> to proceed, <kbd>e</kbd> to edit, <kbd>q</kbd> to abort.</p>
    <h3>4. Watch it work</h3>
    <p>The agent edits files, runs your test suite, and — if everything passes — opens a pull request on a new branch.</p>`},
  'Authenticate Codivus Code': { title: 'Authenticate Codivus Code', section: 'Getting started', read: '2 min read', body: `
    <p>Codivus Code authenticates with a short-lived token tied to your workspace.</p>
    <h3>Log in</h3>
    <pre><code>codivus login</code></pre>
    <p>This opens a browser window and returns a token scoped to the current machine. Tokens can be revoked anytime from <em>Settings → API keys</em>.</p>
    <h3>Headless machines</h3>
    <pre><code>codivus login --token $CODIVUS_TOKEN</code></pre>
    <p>Useful for CI or remote dev containers where a browser isn't available.</p>`},
  'Project configuration': { title: 'Project configuration', section: 'Getting started', read: '3 min read', body: `
    <p>Project behavior is controlled by <code>codivus.json</code> at the repo root. A minimal file looks like this:</p>
    <pre><code>{
  "testCommand": "pnpm test",
  "lintCommand": "pnpm lint",
  "ignore": ["dist", ".next"]
}</code></pre>
    <p>Codivus Code reads this on every run to pick the right tools and avoid wasting time in build artifacts.</p>`},
  'Updating Codivus Code': { title: 'Updating Codivus Code', section: 'Getting started', read: '2 min read', body: `
    <p>Codivus Code ships patches and features continuously. Keep your install current to get the latest model routing, tool support, and bug fixes.</p>
    <h3>Native app</h3>
    <p>Updates download in the background and apply on the next launch. Force a check via <em>Help \u2192 Check for updates</em>. Enterprise MSI deployments can pin a version through Group Policy.</p>
    <h3>VS Code extension</h3>
    <p>The Marketplace auto-updates the extension by default. To update manually, open the Extensions panel, find <em>Codivus Code</em>, and click <em>Update</em>.</p>
    <h3>CLI</h3>
    <pre><code>codivus update</code></pre>
    <p>Or use your package manager:</p>
    <pre><code>npm install -g @codivus/cli@latest</code></pre>
    <h3>Release channels</h3>
    <ul>
      <li><strong>stable</strong> \u2014 default, vetted releases (weekly).</li>
      <li><strong>beta</strong> \u2014 next release candidates (a few times a week).</li>
      <li><strong>nightly</strong> \u2014 latest commit (use at your own risk).</li>
    </ul>
    <pre><code>codivus config set channel beta</code></pre>`},
  'Uninstalling Codivus Code': { title: 'Uninstalling Codivus Code', section: 'Getting started', read: '2 min read', body: `
    <p>Removing Codivus Code is reversible \u2014 your workspace data lives on the server and will be available if you reinstall with the same account.</p>
    <h3>Native app (Windows)</h3>
    <ol>
      <li>Open <em>Settings \u2192 Apps</em>, find <em>Codivus Code</em>, click <em>Uninstall</em>.</li>
      <li>Or from the command line: <code>winget uninstall Codivus Code</code>.</li>
      <li>User data lives at <code>%AppData%\\Codivus Code</code>. Delete it to remove cached sessions.</li>
    </ol>
    <h3>VS Code extension</h3>
    <p>Open the Extensions panel, find <em>Codivus Code</em>, click the gear icon, and choose <em>Uninstall</em>. Reload VS Code when prompted.</p>
    <h3>CLI</h3>
    <pre><code>npm uninstall -g codivus</code></pre>
    <p>Cached auth tokens live at <code>~/.codivus</code> \u2014 remove the folder to clean up completely.</p>
    <h3>Revoke your account</h3>
    <p>To fully deprovision, head to <em>Settings \u2192 API keys</em> and revoke the device token, or close the workspace from <em>Settings \u2192 Organization \u2192 Delete</em>.</p>`},
  'Running tests & fixing failures': { title: 'Running tests & fixing failures', section: 'Guides', read: '5 min read', body: `
    <p>Codivus Code doesn't just run your suite — it reads the failures and iterates.</p>
    <h3>How it works</h3>
    <ol><li>Runs the configured <code>testCommand</code>.</li><li>Parses stderr/stdout for failing assertions.</li><li>Edits the implementation (or the test, if the test is wrong) and re-runs.</li><li>Stops when the suite is green or the iteration budget is reached.</li></ol>
    <h3>Flaky tests</h3>
    <p>If a test fails intermittently, mark it with <code>@flaky</code> and Codivus Code will retry it up to 3 times before treating it as a real failure.</p>`},
  'Opening pull requests': { title: 'Opening pull requests', section: 'Guides', read: '3 min read', body: `
    <p>When Codivus Code finishes a task successfully, it:</p>
    <ul><li>Creates an agent branch (<code>agent/&lt;slug&gt;</code>).</li><li>Commits the diff with a conventional message.</li><li>Pushes to origin.</li><li>Drafts a PR title and body summarizing the change.</li></ul>
    <h3>Required permissions</h3>
    <p>You'll need to grant Codivus Code the <em>contents:write</em> and <em>pull-requests:write</em> scopes in your Git provider. These are requested during <code>codivus init</code>.</p>`},
  'Working with monorepos': { title: 'Working with monorepos', section: 'Guides', read: '4 min read', body: `
    <p>Codivus Code works with pnpm, yarn, turbo, nx, and plain workspaces. The key is scoping each run to the right package.</p>
    <h3>Scoping</h3>
    <pre><code>codivus --scope packages/api "add rate limiting to /login"</code></pre>
    <h3>Cross-package changes</h3>
    <p>When a change spans multiple packages, Codivus Code will open a single PR that touches all of them and note the affected packages in the PR body.</p>`},
  'Using images as input': { title: 'Using images as input', section: 'Guides', read: '2 min read', body: `
    <p>You can drop a screenshot, mockup, or diagram into any Codivus Code task and it becomes part of the agent's context.</p>
    <h3>CLI</h3>
    <pre><code>codivus --image ./mock.png "build this dashboard page"</code></pre>
    <h3>What it understands</h3>
    <p>UI screenshots, architecture diagrams, error screenshots, design mockups, and whiteboard photos all work. The clearer the image, the better the output.</p>`},
  'CLI commands': { title: 'CLI commands', section: 'Reference', read: '3 min read', body: `
    <h3>codivus init</h3><p>Bootstrap the config file in the current repo.</p>
    <h3>codivus login</h3><p>Authenticate the current machine.</p>
    <h3>codivus run "&lt;task&gt;"</h3><p>Run a single task against the repo.</p>
    <h3>codivus watch</h3><p>Start an interactive session with the agent.</p>
    <h3>codivus sessions</h3><p>List recent agent sessions and their outcomes.</p>`},
  'Slash commands': { title: 'Slash commands', section: 'Reference', read: '2 min read', body: `
    <p>Inside an interactive session, slash commands control the agent without leaving the prompt.</p>
    <ul><li><code>/plan</code> — show the current plan</li><li><code>/undo</code> — revert the last edit</li><li><code>/diff</code> — show cumulative changes</li><li><code>/run &lt;cmd&gt;</code> — run a shell command in the sandbox</li><li><code>/stop</code> — end the session without opening a PR</li></ul>`},
  'Settings & permissions': { title: 'Settings & permissions', section: 'Reference', read: '3 min read', body: `
    <p>Every Codivus Code tool call is gated by a permission. Defaults are safe; you can tighten or loosen them per project.</p>
    <h3>Tool scopes</h3>
    <ul><li><code>read</code> — read files and run read-only commands</li><li><code>edit</code> — modify files in the repo</li><li><code>shell</code> — run arbitrary shell commands in the sandbox</li><li><code>network</code> — make outbound HTTP requests</li></ul>`},
  'Webhooks': { title: 'Webhooks', section: 'Reference', read: '3 min read', body: `
    <p>Codivus Code can notify your systems when a session starts, finishes, or opens a PR.</p>
    <h3>Configure an endpoint</h3>
    <pre><code>codivus webhook add https://example.com/hook \\
  --events session.completed,pr.opened</code></pre>
    <h3>Payload</h3>
    <pre><code>{
  "event": "pr.opened",
  "session_id": "sess_4a2f",
  "pr_url": "https://github.com/acme/api/pull/142"
}</code></pre>`},
  'GitHub & GitLab': { title: 'GitHub & GitLab', section: 'Integrations', read: '2 min read', body: `
    <p>Codivus Code authenticates via the official OAuth apps. Install once per organization, then each repo is one-click.</p>
    <h3>What Codivus Code does on your behalf</h3>
    <ul><li>Read repository contents</li><li>Create agent branches</li><li>Push commits</li><li>Open and label pull requests</li></ul>`},
  'VS Code extension': { title: 'VS Code extension', section: 'Integrations', read: '2 min read', body: `
    <p>The Codivus Code VS Code extension brings the agent into your editor. Install it from the Marketplace, then <kbd>Cmd/Ctrl + Shift + C</kbd> to open a session.</p>`},
  'Slack notifications': { title: 'Slack notifications', section: 'Integrations', read: '2 min read', body: `
    <p>Get a Slack message when a Codivus Code session finishes or opens a PR. Connect your workspace from <em>Settings → Integrations</em>.</p>`},
  'Bring-your-own-model': { title: 'Bring-your-own-model', section: 'Integrations', read: '3 min read', body: `
    <p>Pro and above can route Codivus Code to their own model provider. This gives you direct billing, model choice, and data-residency control.</p>
    <pre><code>codivus config set model.provider anthropic
codivus config set model.key $ANTHROPIC_API_KEY</code></pre>`},
};

export function renderDocs(): string {
  const sections = [
    { title: 'Getting started', items: ['Install the CLI', 'Quickstart: your first task', 'Authenticate Codivus Code', 'Project configuration', 'Updating Codivus Code', 'Uninstalling Codivus Code'] },
    { title: 'Guides',          items: ['Running tests & fixing failures', 'Opening pull requests', 'Working with monorepos', 'Using images as input'] },
    { title: 'Reference',       items: ['CLI commands', 'Slash commands', 'Settings & permissions', 'Webhooks'] },
    { title: 'Integrations',    items: ['GitHub & GitLab', 'VS Code extension', 'Slack notifications', 'Bring-your-own-model'] },
  ];
  const group = (g: typeof sections[0]) => `
    <div class="docs-group">
      <h3>${g.title}</h3>
      <ul>${g.items.map(i => `<li><a href="#" data-docs-link="${escHtml(i)}">${i}</a></li>`).join('')}</ul>
    </div>`;
  const inner = `
<section class="generic-section">
  <header class="generic-header">
    <h1>Documentation</h1>
    <p>Guides, references, and integrations for Codivus Code. New pages land every week.</p>
  </header>
  <div class="docs-search">
    <input class="form-input" placeholder="Search docs\u2026" id="docsSearch" />
  </div>
  <div class="docs-grid">
    ${sections.map(group).join('')}
  </div>
</section>

<div class="doc-modal" id="docModal" aria-hidden="true" role="dialog" aria-modal="true">
  <div class="doc-modal-backdrop" data-doc-close></div>
  <div class="doc-modal-panel" role="document">
    <button class="doc-modal-close" data-doc-close aria-label="Close">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="6" y1="18" x2="18" y2="6"/></svg>
    </button>
    <div class="doc-modal-body" id="docModalBody"></div>
  </div>
</div>`;
  return publicShell(inner, '');
}

export function initDocs(): void {
  bindPublicNav();
  const modal = document.getElementById('docModal');
  const body  = document.getElementById('docModalBody');
  const open = (key: string) => {
    const article = DOC_ARTICLES[key];
    if (!modal || !body) return;
    if (!article) {
      toast('info', key, 'Full article coming shortly.');
      return;
    }
    body.innerHTML = `
      <div class="doc-meta"><span class="doc-section">${escHtml(article.section)}</span></div>
      <h1 class="doc-title">${escHtml(article.title)}</h1>
      <div class="doc-content">${article.body}</div>
      <div class="doc-footer">
        <span>Was this helpful?</span>
        <button class="btn btn-ghost" data-doc-feedback="yes">\u{1F44D} Yes</button>
        <button class="btn btn-ghost" data-doc-feedback="no">\u{1F44E} No</button>
      </div>`;
    body.querySelectorAll<HTMLButtonElement>('[data-doc-feedback]').forEach(b => {
      b.addEventListener('click', () => toast('success', 'Thanks', 'Feedback noted.'));
    });
    modal.classList.add('open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  };
  const close = () => {
    if (!modal) return;
    modal.classList.remove('open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  };
  document.querySelectorAll<HTMLAnchorElement>('[data-docs-link]').forEach(a => {
    a.addEventListener('click', (e) => {
      e.preventDefault();
      open(a.dataset.docsLink || '');
    });
  });
  modal?.querySelectorAll<HTMLElement>('[data-doc-close]').forEach(el => { el.addEventListener('click', close); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
}

// ---- Blog ----

export function renderBlog(): string {
  const posts = [
    {
      tag: 'Founder’s note',
      date: 'Apr 18, 2026',
      title: 'Why we started building Codivus Code',
      excerpt: 'The honest story behind Codivus Code: shared token budgets, expensive agent workflows, and a belief that African engineers deserve tools designed around our realities.',
      active: true,
      body: `
        <p>Codivus Code did not begin as a polished product roadmap. It started with a practical frustration that many developers will recognize: I was using another coding agent, getting real value from it, and then noticing the token bill climb faster than expected.</p>
        <p>The difficult part was that I was not using those tokens alone. I shared access with friends, and it became clear that my usage was taking far more of the shared allowance than theirs. Every long coding session, every retry, every large context window had a cost attached to it. The tool was helpful, but the economics made it feel distant from the way many of us actually work.</p>
        <p>That was the moment the idea became personal. Instead of only asking how to use a coding agent better, I started asking a different question: what would it take to build one that is genuinely cost-effective for me, for my friends, and for developers across Malawi and the wider African community?</p>
        <p>For many African engineers, the challenge is not talent or ambition. It is the cost of access. In Malawi, that cost is not only about subscription prices. It is also about foreign exchange: paying for global developer tools often means finding scarce forex, absorbing exchange-rate pressure, and navigating payment systems that were not designed for local realities. We often build with expensive internet, unstable connectivity, shared machines, limited budgets, and platforms that assume international payments are simple. A powerful AI coding tool is exciting, but if it quietly assumes unlimited bandwidth, unlimited tokens, unlimited money, and easy access to dollars, then it excludes exactly the people who could benefit from it most.</p>
        <p>Codivus Code was born from that tension. We wanted an agent that could help engineers plan, edit, run tools, and ship real work while staying conscious of cost. That means thinking carefully about context, avoiding wasteful loops, making sessions more efficient, and building workflows that do not punish people for learning, experimenting, or working on slower networks.</p>
        <p>I am proud, and honestly excited, that the idea worked. What began as a personal attempt to solve my own token problem has grown into a product with a bigger mission: to make capable coding assistance more accessible to developers who are often priced out of the newest tools.</p>
        <p>We are still early. The next chapter is about making Codivus Code resonate even more with the African context: better behavior on unreliable internet, more cost-aware defaults, stronger support for local teams, and product decisions that respect the financial realities of the people using it.</p>
        <p>That is the heart of Codivus Code. It is not just another coding agent. It is a bet that world-class engineering tools can be built from here, shaped by our constraints, and still compete anywhere.</p>`,
    },
    { tag: 'Engineering', date: 'Apr 10, 2026', title: 'How Codivus Code plans before it edits', excerpt: 'A look at the planning loop: why letting the agent think explicitly produces cleaner diffs than reactive editing.', active: false },
    { tag: 'Product',     date: 'Apr 2, 2026',  title: 'Parallel sessions for big refactors', excerpt: 'Running multiple isolated agent sessions on the same repo without config drift.', active: false },
    { tag: 'Case study',  date: 'Mar 26, 2026', title: 'Shipping a migration in a weekend', excerpt: 'A small team used Codivus Code to move 40 services off a deprecated RPC layer in two days.', active: false },
    { tag: 'Changelog',   date: 'Mar 18, 2026', title: 'Image understanding is now live',    excerpt: 'Drop a screenshot into a task and let the agent ground its work in the real UI.', active: false },
    { tag: 'Community',   date: 'Mar 4, 2026',  title: 'Office hours, every Thursday',       excerpt: 'Hop on with the team — share feedback, watch demos, ask hard questions.', active: false },
  ];
  const activePost = posts.find(p => p.active) ?? posts[0];
  const card = (p: typeof posts[0]) => `
    <article class="blog-card${p.active ? ' active' : ' inactive'}">
      <div class="blog-meta"><span class="blog-tag">${p.tag}</span><span class="blog-date">${p.date}</span></div>
      <h3>${p.active ? `<a href="#origin-story" data-blog-link="${p.title}">${p.title}</a>` : p.title}</h3>
      <p>${p.excerpt}</p>
      ${p.active
        ? `<a class="blog-more" href="#origin-story" data-blog-link="${p.title}">Read the story \u2192</a>`
        : `<span class="blog-more muted">Coming soon</span>`}
    </article>`;
  const inner = `
<section class="generic-section">
  <header class="generic-header">
    <h1>Blog</h1>
    <p>Notes from the team — how Codivus Code is built, what we're learning, and how teams ship with it.</p>
  </header>
  <div class="blog-grid">
    ${posts.map(card).join('')}
  </div>
  <article class="blog-article" id="origin-story">
    <div class="blog-meta"><span class="blog-tag">${activePost.tag}</span><span class="blog-date">${activePost.date}</span></div>
    <h2>${activePost.title}</h2>
    <p class="blog-article-lede">A coding agent for African builders has to respect the realities of cost, connectivity, shared access, and forex constraints. That conviction shaped Codivus Code from the beginning.</p>
    <div class="blog-article-body">${activePost.body ?? ''}</div>
  </article>
</section>`;
  return publicShell(inner, '');
}

export function initBlog(): void {
  bindPublicNav();
  document.querySelectorAll<HTMLAnchorElement>('[data-blog-link]').forEach(a => {
    a.addEventListener('click', (e) => {
      e.preventDefault();
      document.getElementById('origin-story')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });
}

// ---- Demos ----
//
// A gallery of sample projects that Codivus Code has produced. Each card is a
// preview tile with a generated gradient thumbnail (no external assets),
// a short prompt summary, tech badges, and an "Open demo" action. Demo
// URLs that aren't live yet just toast "coming soon" — keeps the gallery
// scannable without blockers.

// Mini-preview SVGs — each one is a scaled-down wireframe of the actual
// layout rendered in the theme colors. No external assets, fully inline.
const DEMO_PREVIEWS: Record<string, string> = {
  'saas-landing': `
    <svg viewBox="0 0 400 250" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
      <rect width="400" height="250" fill="#0f172a"/>
      <rect x="0" y="0" width="400" height="32" fill="#1e293b"/>
      <rect x="14" y="12" width="60" height="8" rx="2" fill="#8b5cf6"/>
      <rect x="310" y="11" width="32" height="10" rx="3" fill="#334155"/>
      <rect x="348" y="9" width="40" height="14" rx="7" fill="#6366f1"/>
      <rect x="60" y="62" width="280" height="14" rx="2" fill="#cbd5e1"/>
      <rect x="100" y="82" width="200" height="8" rx="2" fill="#94a3b8"/>
      <rect x="140" y="104" width="56" height="18" rx="9" fill="#6366f1"/>
      <rect x="204" y="104" width="56" height="18" rx="9" fill="#334155"/>
      <rect x="40" y="152" width="100" height="74" rx="6" fill="#1e293b" stroke="#334155"/>
      <rect x="150" y="152" width="100" height="74" rx="6" fill="#1e293b" stroke="#334155"/>
      <rect x="260" y="152" width="100" height="74" rx="6" fill="#1e293b" stroke="#334155"/>
      <rect x="52" y="166" width="24" height="24" rx="4" fill="#6366f1" opacity="0.5"/>
      <rect x="162" y="166" width="24" height="24" rx="4" fill="#8b5cf6" opacity="0.5"/>
      <rect x="272" y="166" width="24" height="24" rx="4" fill="#a855f7" opacity="0.5"/>
      <rect x="52" y="196" width="60" height="6" rx="2" fill="#64748b"/>
      <rect x="162" y="196" width="60" height="6" rx="2" fill="#64748b"/>
      <rect x="272" y="196" width="60" height="6" rx="2" fill="#64748b"/>
      <rect x="52" y="208" width="80" height="4" rx="2" fill="#475569"/>
      <rect x="162" y="208" width="80" height="4" rx="2" fill="#475569"/>
      <rect x="272" y="208" width="80" height="4" rx="2" fill="#475569"/>
    </svg>`,
  'admin-dashboard': `
    <svg viewBox="0 0 400 250" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
      <rect width="400" height="250" fill="#f8fafc"/>
      <rect x="0" y="0" width="80" height="250" fill="#0f172a"/>
      <rect x="12" y="14" width="56" height="10" rx="3" fill="#0ea5e9"/>
      <rect x="12" y="40" width="56" height="8" rx="2" fill="#0ea5e9" opacity="0.4"/>
      <rect x="12" y="56" width="56" height="8" rx="2" fill="#334155"/>
      <rect x="12" y="72" width="56" height="8" rx="2" fill="#334155"/>
      <rect x="12" y="88" width="56" height="8" rx="2" fill="#334155"/>
      <rect x="92" y="14" width="60" height="52" rx="6" fill="#ffffff" stroke="#e2e8f0"/>
      <rect x="160" y="14" width="60" height="52" rx="6" fill="#ffffff" stroke="#e2e8f0"/>
      <rect x="228" y="14" width="60" height="52" rx="6" fill="#ffffff" stroke="#e2e8f0"/>
      <rect x="296" y="14" width="60" height="52" rx="6" fill="#ffffff" stroke="#e2e8f0"/>
      <rect x="100" y="26" width="36" height="6" rx="2" fill="#94a3b8"/>
      <rect x="168" y="26" width="36" height="6" rx="2" fill="#94a3b8"/>
      <rect x="236" y="26" width="36" height="6" rx="2" fill="#94a3b8"/>
      <rect x="304" y="26" width="36" height="6" rx="2" fill="#94a3b8"/>
      <text x="100" y="56" font-size="14" font-weight="700" fill="#0ea5e9">12.4k</text>
      <text x="168" y="56" font-size="14" font-weight="700" fill="#06b6d4">$2.8k</text>
      <text x="236" y="56" font-size="14" font-weight="700" fill="#22c55e">98%</text>
      <text x="304" y="56" font-size="14" font-weight="700" fill="#f97316">74</text>
      <rect x="92" y="80" width="264" height="100" rx="6" fill="#ffffff" stroke="#e2e8f0"/>
      <polyline points="100,160 140,140 170,150 200,120 230,130 260,100 290,110 320,90 350,105" fill="none" stroke="#0ea5e9" stroke-width="2"/>
      <polyline points="100,160 140,140 170,150 200,120 230,130 260,100 290,110 320,90 350,105 350,175 100,175" fill="#0ea5e9" opacity="0.15"/>
      <rect x="92" y="192" width="264" height="46" rx="6" fill="#ffffff" stroke="#e2e8f0"/>
      <rect x="100" y="202" width="80" height="5" rx="2" fill="#cbd5e1"/>
      <rect x="100" y="214" width="200" height="4" rx="2" fill="#e2e8f0"/>
      <rect x="100" y="224" width="200" height="4" rx="2" fill="#e2e8f0"/>
    </svg>`,
  'ecommerce': `
    <svg viewBox="0 0 400 250" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
      <rect width="400" height="250" fill="#fff7ed"/>
      <rect x="0" y="0" width="400" height="36" fill="#ffffff" stroke="#fed7aa"/>
      <rect x="14" y="13" width="50" height="10" rx="3" fill="#ea580c"/>
      <rect x="200" y="13" width="30" height="10" rx="2" fill="#fdba74"/>
      <rect x="240" y="13" width="30" height="10" rx="2" fill="#fdba74"/>
      <rect x="280" y="13" width="30" height="10" rx="2" fill="#fdba74"/>
      <rect x="350" y="9" width="36" height="18" rx="9" fill="#f97316"/>
      <rect x="14" y="50" width="372" height="30" rx="4" fill="#ffffff" stroke="#fed7aa"/>
      <rect x="24" y="60" width="60" height="10" rx="2" fill="#fdba74"/>
      <rect x="96" y="60" width="60" height="10" rx="2" fill="#fdba74"/>
      <rect x="168" y="60" width="60" height="10" rx="2" fill="#fdba74"/>
      ${[0, 1, 2, 3, 4, 5].map(i => {
        const col = i % 3;
        const row = Math.floor(i / 3);
        const x = 14 + col * 128;
        const y = 92 + row * 74;
        const hue = ['#f97316', '#ef4444', '#dc2626', '#fb923c', '#f59e0b', '#ef4444'][i];
        return `
        <rect x="${x}" y="${y}" width="120" height="66" rx="6" fill="#ffffff" stroke="#fed7aa"/>
        <rect x="${x + 6}" y="${y + 6}" width="38" height="54" rx="4" fill="${hue}" opacity="0.3"/>
        <rect x="${x + 50}" y="${y + 10}" width="60" height="6" rx="2" fill="#1f2937"/>
        <rect x="${x + 50}" y="${y + 22}" width="44" height="4" rx="2" fill="#9ca3af"/>
        <rect x="${x + 50}" y="${y + 40}" width="30" height="10" rx="3" fill="${hue}"/>`;
      }).join('')}
    </svg>`,
  'blog-cms': `
    <svg viewBox="0 0 400 250" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
      <rect width="400" height="250" fill="#f0fdf4"/>
      <rect x="0" y="0" width="400" height="34" fill="#ffffff" stroke="#bbf7d0"/>
      <rect x="14" y="11" width="70" height="12" rx="3" fill="#16a34a"/>
      <rect x="320" y="10" width="66" height="14" rx="7" fill="#22c55e"/>
      <rect x="14" y="52" width="372" height="26" rx="4" fill="transparent"/>
      <text x="14" y="70" font-size="18" font-weight="800" fill="#14532d">Latest posts</text>
      ${[0, 1, 2].map(i => {
        const y = 92 + i * 50;
        return `
        <rect x="14" y="${y}" width="372" height="44" rx="6" fill="#ffffff" stroke="#bbf7d0"/>
        <rect x="24" y="${y + 10}" width="48" height="24" rx="4" fill="#22c55e" opacity="0.25"/>
        <rect x="80" y="${y + 10}" width="240" height="8" rx="2" fill="#14532d"/>
        <rect x="80" y="${y + 24}" width="180" height="5" rx="2" fill="#86efac"/>
        <rect x="336" y="${y + 16}" width="40" height="12" rx="6" fill="#dcfce7"/>`;
      }).join('')}
    </svg>`,
  'docs-site': `
    <svg viewBox="0 0 400 250" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
      <rect width="400" height="250" fill="#0f172a"/>
      <rect x="0" y="0" width="400" height="32" fill="#1e293b"/>
      <rect x="14" y="11" width="70" height="10" rx="3" fill="#e2e8f0"/>
      <rect x="220" y="9" width="140" height="14" rx="7" fill="#334155"/>
      <rect x="0" y="32" width="100" height="218" fill="#1e293b"/>
      ${[0, 1, 2, 3, 4, 5, 6].map(i => {
        const y = 48 + i * 22;
        const active = i === 2;
        return `<rect x="10" y="${y}" width="${active ? 80 : 70}" height="8" rx="2" fill="${active ? '#38bdf8' : '#475569'}"/>`;
      }).join('')}
      <rect x="116" y="48" width="200" height="14" rx="3" fill="#e2e8f0"/>
      <rect x="116" y="72" width="268" height="6" rx="2" fill="#94a3b8"/>
      <rect x="116" y="86" width="260" height="6" rx="2" fill="#94a3b8"/>
      <rect x="116" y="100" width="220" height="6" rx="2" fill="#94a3b8"/>
      <rect x="116" y="122" width="268" height="72" rx="6" fill="#0b1220" stroke="#1e293b"/>
      ${[0, 1, 2, 3, 4].map(i => {
        const y = 132 + i * 12;
        const w = [180, 230, 160, 210, 140][i];
        return `<rect x="124" y="${y}" width="${w}" height="5" rx="2" fill="#38bdf8" opacity="${0.3 + i * 0.1}"/>`;
      }).join('')}
      <rect x="116" y="204" width="180" height="6" rx="2" fill="#94a3b8"/>
      <rect x="116" y="218" width="220" height="6" rx="2" fill="#94a3b8"/>
    </svg>`,
  'chat-ui': `
    <svg viewBox="0 0 400 250" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
      <rect width="400" height="250" fill="#faf5ff"/>
      <rect x="0" y="0" width="110" height="250" fill="#ffffff" stroke="#e9d5ff"/>
      <rect x="10" y="14" width="90" height="22" rx="6" fill="#a855f7"/>
      ${[0, 1, 2, 3, 4].map(i => {
        const y = 50 + i * 32;
        const active = i === 1;
        return `
        <rect x="10" y="${y}" width="90" height="26" rx="4" fill="${active ? '#f3e8ff' : 'transparent'}"/>
        <rect x="16" y="${y + 8}" width="${60 - i * 6}" height="5" rx="2" fill="#6b21a8"/>
        <rect x="16" y="${y + 17}" width="${70 - i * 5}" height="4" rx="2" fill="#c084fc"/>`;
      }).join('')}
      <rect x="118" y="12" width="270" height="20" rx="4" fill="#f3e8ff"/>
      <rect x="126" y="18" width="60" height="8" rx="2" fill="#7e22ce"/>
      <rect x="118" y="44" width="180" height="26" rx="6" fill="#ffffff" stroke="#e9d5ff"/>
      <rect x="126" y="52" width="130" height="10" rx="2" fill="#6b21a8"/>
      <rect x="220" y="82" width="168" height="36" rx="6" fill="#a855f7"/>
      <rect x="228" y="90" width="120" height="6" rx="2" fill="#f3e8ff"/>
      <rect x="228" y="100" width="100" height="6" rx="2" fill="#e9d5ff"/>
      <rect x="118" y="130" width="220" height="48" rx="6" fill="#ffffff" stroke="#e9d5ff"/>
      <rect x="126" y="138" width="180" height="6" rx="2" fill="#6b21a8"/>
      <rect x="126" y="150" width="160" height="6" rx="2" fill="#9333ea"/>
      <rect x="126" y="162" width="120" height="6" rx="2" fill="#c084fc"/>
      <rect x="118" y="206" width="270" height="30" rx="15" fill="#ffffff" stroke="#c084fc"/>
      <rect x="130" y="218" width="180" height="6" rx="2" fill="#d8b4fe"/>
      <circle cx="368" cy="221" r="10" fill="#a855f7"/>
    </svg>`,
  'task-tracker': `
    <svg viewBox="0 0 400 250" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
      <rect width="400" height="250" fill="#fffbeb"/>
      <rect x="0" y="0" width="400" height="32" fill="#ffffff" stroke="#fde68a"/>
      <rect x="14" y="10" width="60" height="12" rx="3" fill="#d97706"/>
      <rect x="340" y="9" width="46" height="14" rx="7" fill="#eab308"/>
      ${[
        { x: 14, color: '#fbbf24', label: 'Todo', count: 4, bg: '#fef3c7' },
        { x: 142, color: '#f97316', label: 'Doing', count: 3, bg: '#fed7aa' },
        { x: 270, color: '#22c55e', label: 'Done', count: 5, bg: '#bbf7d0' },
      ].map(col => `
        <rect x="${col.x}" y="46" width="116" height="196" rx="8" fill="${col.bg}" opacity="0.5"/>
        <rect x="${col.x + 10}" y="56" width="50" height="8" rx="2" fill="${col.color}"/>
        ${[0, 1, 2].map(i => {
          const y = 76 + i * 42;
          return `
          <rect x="${col.x + 8}" y="${y}" width="100" height="34" rx="5" fill="#ffffff" stroke="${col.color}" opacity="0.9"/>
          <rect x="${col.x + 16}" y="${y + 8}" width="60" height="5" rx="2" fill="#1f2937"/>
          <rect x="${col.x + 16}" y="${y + 18}" width="40" height="4" rx="2" fill="#9ca3af"/>
          <circle cx="${col.x + 100}" cy="${y + 24}" r="5" fill="${col.color}"/>`;
        }).join('')}
      `).join('')}
    </svg>`,
  'portfolio': `
    <svg viewBox="0 0 400 250" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
      <rect width="400" height="250" fill="#fafafa"/>
      <rect x="0" y="0" width="400" height="32" fill="#ffffff" stroke="#e5e7eb"/>
      <circle cx="24" cy="16" r="8" fill="#1f2937"/>
      <rect x="40" y="12" width="60" height="8" rx="2" fill="#1f2937"/>
      <rect x="280" y="12" width="30" height="8" rx="2" fill="#6b7280"/>
      <rect x="316" y="12" width="30" height="8" rx="2" fill="#6b7280"/>
      <rect x="352" y="9" width="36" height="14" rx="7" fill="#1f2937"/>
      <text x="40" y="74" font-size="22" font-weight="800" fill="#111827">Hi, I'm Alex.</text>
      <rect x="40" y="86" width="280" height="5" rx="2" fill="#6b7280"/>
      <rect x="40" y="98" width="220" height="5" rx="2" fill="#6b7280"/>
      <rect x="40" y="116" width="84" height="16" rx="8" fill="#111827"/>
      ${[0, 1, 2, 3].map(i => {
        const col = i % 4;
        const x = 14 + col * 96;
        const y = 150;
        const hue = ['#60a5fa', '#f472b6', '#34d399', '#fbbf24'][i];
        return `
        <rect x="${x}" y="${y}" width="88" height="72" rx="6" fill="#ffffff" stroke="#e5e7eb"/>
        <rect x="${x + 4}" y="${y + 4}" width="80" height="40" rx="4" fill="${hue}" opacity="0.85"/>
        <rect x="${x + 4}" y="${y + 50}" width="64" height="5" rx="2" fill="#1f2937"/>
        <rect x="${x + 4}" y="${y + 60}" width="48" height="4" rx="2" fill="#9ca3af"/>`;
      }).join('')}
    </svg>`,
  'markdown-editor': `
    <svg viewBox="0 0 400 250" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
      <rect width="400" height="250" fill="#0b1220"/>
      <rect x="0" y="0" width="400" height="28" fill="#1e293b"/>
      <rect x="12" y="9" width="48" height="10" rx="3" fill="#14b8a6"/>
      <rect x="340" y="8" width="48" height="12" rx="6" fill="#3b82f6"/>
      <rect x="0" y="28" width="200" height="222" fill="#0b1220"/>
      <rect x="200" y="28" width="200" height="222" fill="#f8fafc"/>
      ${[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(i => {
        const y = 48 + i * 18;
        const colors = ['#14b8a6', '#94a3b8', '#94a3b8', '#3b82f6', '#94a3b8', '#94a3b8', '#f59e0b', '#94a3b8', '#94a3b8', '#94a3b8'];
        const widths = [140, 160, 100, 120, 150, 90, 60, 170, 140, 110];
        return `<rect x="14" y="${y}" width="${widths[i]}" height="6" rx="2" fill="${colors[i]}"/>`;
      }).join('')}
      <text x="210" y="66" font-size="16" font-weight="800" fill="#0f172a">Preview</text>
      <rect x="210" y="84" width="180" height="5" rx="2" fill="#334155"/>
      <rect x="210" y="96" width="160" height="5" rx="2" fill="#334155"/>
      <rect x="210" y="108" width="170" height="5" rx="2" fill="#334155"/>
      <rect x="210" y="128" width="140" height="8" rx="2" fill="#3b82f6"/>
      <rect x="210" y="148" width="170" height="5" rx="2" fill="#334155"/>
      <rect x="210" y="160" width="160" height="5" rx="2" fill="#334155"/>
      <rect x="210" y="180" width="100" height="20" rx="4" fill="#e2e8f0"/>
      <rect x="216" y="186" width="88" height="8" rx="2" fill="#f59e0b" opacity="0.4"/>
    </svg>`,
};

export function renderDemos(): string {
  // previewUrl: a same-origin page rendered into the card's iframe thumbnail
  // (external sites almost always ship X-Frame-Options: DENY). liveUrl: the
  // real demo deploy the card opens on click. None are published yet, so
  // every liveUrl is blank — the card shows a "Coming soon" badge and clicks
  // toast instead of opening. Fill in liveUrl when a demo deploy goes live.
  const demos = [
    { id: 'saas-landing',     title: 'SaaS landing page',      prompt: 'Marketing site for an observability startup with hero, feature grid, pricing, and email capture.', tech: ['React', 'Tailwind', 'Next.js'],            previewUrl: '/features',   liveUrl: '' },
    { id: 'admin-dashboard',  title: 'Analytics dashboard',    prompt: 'Sidebar nav, KPI cards, time-series charts, and a filterable data table with pagination.',         tech: ['TypeScript', 'Vite', 'Recharts'],           previewUrl: '/pricing',    liveUrl: '' },
    { id: 'ecommerce',        title: 'E-commerce storefront',  prompt: 'Product grid with category filters, detail pages, a cart drawer, and a checkout stub.',            tech: ['Next.js', 'Stripe', 'Prisma'],              previewUrl: '/install',    liveUrl: '' },
    { id: 'blog-cms',         title: 'Blog with CMS',          prompt: 'MDX-driven blog with tag pages, RSS feed, dark mode, and an admin view for drafts.',               tech: ['Astro', 'MDX', 'Tailwind'],                 previewUrl: '/blog',       liveUrl: '' },
    { id: 'docs-site',        title: 'Developer docs site',    prompt: 'API reference + guides layout with search, collapsible sections, and live code samples.',          tech: ['Docusaurus', 'Algolia', 'MDX'],             previewUrl: '/docs',       liveUrl: '' },
    { id: 'chat-ui',          title: 'AI chat app',            prompt: 'Multi-thread chat UI with streaming responses, file uploads, and a model-selection drawer.',       tech: ['React', 'tRPC', 'Anthropic SDK'],           previewUrl: '/',           liveUrl: '' },
    { id: 'task-tracker',     title: 'Task tracker',           prompt: 'Kanban board with drag-and-drop columns, per-task detail modal, labels, and a team filter.',       tech: ['Vue 3', 'Pinia', 'dnd-kit'],                previewUrl: '/enterprise', liveUrl: '' },
    { id: 'markdown-editor',  title: 'Markdown editor',        prompt: 'Split-pane editor with live preview, keyboard shortcuts, and export to HTML / PDF.',               tech: ['Svelte', 'marked', 'Prism'],                previewUrl: '/enterprise', liveUrl: '' },
    { id: 'portfolio',        title: 'Portfolio site',         prompt: 'Personal portfolio with a hero intro, project grid, case-study pages, and a contact form.',        tech: ['Next.js', 'Framer Motion', 'Vercel'],       previewUrl: '/features',   liveUrl: '' },
  ];

  // Always include the SVG wireframe so mobile/blocked/slow-loading iframes
  // never leave a blank card. When a url is set, the live iframe layers on
  // top and covers the SVG; CSS hides the iframe on narrow viewports where
  // mobile browsers handle scaled iframes unreliably.
  // HTML width/height attributes + scrolling="no" are what iOS Safari
  // actually respects for iframe sizing — CSS width on iframes is ignored
  // by Safari. The transform scale is computed in initDemos() so the
  // 1440×900 desktop render shrinks to fit the card on any device.
  const preview = (d: typeof demos[0]) => {
    const svg = DEMO_PREVIEWS[d.id] ?? '';
    const iframe = d.previewUrl
      ? `<iframe class="demo-iframe" src="${escHtml(d.previewUrl)}" width="1440" height="900" scrolling="no" loading="lazy" referrerpolicy="no-referrer" sandbox="allow-scripts allow-same-origin" title="${escHtml(d.title)} preview" tabindex="-1"></iframe>`
      : '';
    return svg + iframe;
  };

  const card = (d: typeof demos[0]) => `
    <article class="demo-card ${d.liveUrl ? 'demo-card-live' : ''}" data-demo-open="${d.id}" ${d.liveUrl ? `data-url="${d.liveUrl}"` : ''} title="${escHtml(d.title)}">
      <div class="demo-thumb">
        ${preview(d)}
        <div class="demo-thumb-chrome">
          <span></span><span></span><span></span>
        </div>
        ${d.liveUrl ? '' : `<span class="badge yellow demo-soon-badge">Coming soon</span>`}
        <div class="demo-thumb-tech">
          ${d.tech.map(t => `<span class="demo-chip demo-chip-overlay">${escHtml(t)}</span>`).join('')}
        </div>
      </div>
    </article>`;

  const inner = `
<section class="generic-section">
  <header class="generic-header">
    <h1>Demos</h1>
    <p>Live examples produced by Codivus Code from a single prompt. Open one to see the shipped site, or fork the prompt to build your own.</p>
  </header>
  <div class="demo-grid">
    ${demos.map(card).join('')}
  </div>
  <div style="text-align:center;margin-top:48px">
    <a class="btn btn-filled" href="${platformUrl()}" data-public-nav="register">Start building \u2192</a>
  </div>
</section>`;
  return publicShell(inner, '');
}

export function initDemos(): void {
  bindPublicNav();
  document.querySelectorAll<HTMLElement>('[data-demo-open]').forEach(el => {
    el.addEventListener('click', () => {
      const url = el.dataset.url;
      if (url) {
        window.open(url, '_blank', 'noopener');
      } else {
        toast('info', 'Coming soon', 'Live demo URL not published yet.');
      }
    });
  });

  // Scale each iframe so the 1440×900 desktop render fits its card. Works
  // on mobile Safari because we use HTML width/height attributes (which
  // iOS respects) plus transform: scale via inline style. Re-applied on
  // resize, on iframe load, and after paint to handle late layout.
  const iframes = document.querySelectorAll<HTMLIFrameElement>('.demo-iframe');
  if (!iframes.length) return;

  const applyScale = (iframe: HTMLIFrameElement): void => {
    const thumb = iframe.parentElement;
    if (!thumb) return;
    const w = thumb.clientWidth;
    if (w <= 0) return;
    const scale = w / 1440;
    iframe.style.transform = `scale(${scale})`;
    iframe.style.transformOrigin = 'top left';
  };
  const applyAll = () => iframes.forEach(applyScale);

  const ro = new ResizeObserver(() => applyAll());
  iframes.forEach(iframe => {
    applyScale(iframe);
    iframe.addEventListener('load', () => {
      applyScale(iframe);
      iframe.classList.add('loaded');
    });
    if (iframe.parentElement) ro.observe(iframe.parentElement);
  });
  window.addEventListener('resize', applyAll, { passive: true });
  requestAnimationFrame(() => requestAnimationFrame(applyAll));
}

// ---- Enterprise ----

export function renderEnterprise(): string {
  const pillars = [
    { icon: '\u{1F512}', title: 'Deploy in your VPC',      body: 'Run Codivus Code in your own cloud account with hardened defaults — no agent traffic leaves your perimeter.' },
    { icon: '\u{1F4C4}', title: 'SOC 2 & custom DPAs',     body: 'Security reviews, DPAs, and subprocessor lists your legal team will actually approve.' },
    { icon: '\u{1F465}', title: 'SSO & role controls',     body: 'Google, Okta, Azure AD. Per-project roles, audit logs, and scoped API tokens.' },
    { icon: '\u{1F504}', title: 'Bring-your-own-model',    body: 'Route to your contracted model provider, on-prem inference, or per-tenant model keys.' },
    { icon: '\u{1F4DE}', title: 'Dedicated support',       body: 'A named success engineer, 99.9% SLA, and direct Slack channel with the Codivus Code team.' },
    { icon: '\u{1F4CA}', title: 'Usage analytics',         body: 'Per-team dashboards, cost attribution, and exportable run logs for internal reporting.' },
  ];
  const tile = (p: typeof pillars[0]) => `
    <article class="feature-card">
      <span class="feature-icon">${p.icon}</span>
      <h3>${p.title}</h3>
      <p>${p.body}</p>
    </article>`;
  const inner = `
<section class="generic-section">
  <header class="generic-header">
    <h1>Codivus Code for Enterprise</h1>
    <p>Self-hosted or in our cloud, with the security, compliance, and support large teams need.</p>
  </header>
  <div class="feature-grid">
    ${pillars.map(tile).join('')}
  </div>
  <div class="enterprise-contact">
    <h2>Talk to sales</h2>
    <p>Tell us about your team and we\u2019ll come back within a business day.</p>
    <form class="enterprise-form" id="enterpriseForm">
      <div class="form-row">
        <div class="form-field"><label class="form-label">Work email</label><input class="form-input" type="email" id="entEmail" required placeholder="you@company.com" /></div>
        <div class="form-field"><label class="form-label">Company</label><input class="form-input" id="entCompany" required placeholder="Acme Inc." /></div>
      </div>
      <div class="form-row">
        <div class="form-field"><label class="form-label">Team size</label>
          <select class="form-input" id="entSize">
            <option>1\u201310</option><option>11\u201350</option><option>51\u2013200</option><option>200+</option>
          </select>
        </div>
        <div class="form-field"><label class="form-label">What are you hoping to ship?</label><input class="form-input" id="entNeed" placeholder="e.g. migrate monorepo off legacy API" /></div>
      </div>
      <button class="btn btn-filled" type="submit">Request a call</button>
    </form>
  </div>
</section>`;
  return publicShell(inner, '');
}

// ---- Install ----

type NativeOs = 'win' | 'linux';

const OS_ICON: Record<NativeOs, string> = {
  win: `<svg width="22" height="22" viewBox="0 0 24 24" fill="#0078D4" xmlns="http://www.w3.org/2000/svg"><path d="M0 3.45L9.75 2.1V11.4H0V3.45zM10.95 1.95L24 0V11.25H10.95V1.95zM0 12.6H9.75V21.9L0 20.55V12.6zM10.95 12.75H24V24L10.95 22.05V12.75z"/></svg>`,
  linux: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="#111" d="M12.1 2.2c-2.5 0-4.2 2-4.2 5.1 0 1.3-.4 2.5-1.1 3.6L4.2 15c-.8 1.3-.4 3 1 3.8l2.4 1.3c1.1.6 2.4.9 3.7.9h1.4c1.3 0 2.6-.3 3.7-.9l2.4-1.3c1.4-.8 1.8-2.5 1-3.8l-2.6-4.1c-.7-1.1-1.1-2.3-1.1-3.6 0-3.1-1.6-5.1-4-5.1z"/><path fill="#fff" d="M9.7 7.2c.6 0 1.1-.6 1.1-1.3s-.5-1.3-1.1-1.3-1.1.6-1.1 1.3.5 1.3 1.1 1.3zm4.6 0c.6 0 1.1-.6 1.1-1.3s-.5-1.3-1.1-1.3-1.1.6-1.1 1.3.5 1.3 1.1 1.3z"/><path fill="#F9C23C" d="M10.2 8.6h3.6L12 10.4 10.2 8.6z"/><path fill="#F9C23C" d="M7.7 18.9c-.8-.2-1.3-.7-1.4-1.4-.2-.8.2-1.4.8-1.8l1.4-.8 1.1 2.8-1.9 1.2zm8.6 0 1.9-1.2c.6-.4 1-1 .8-1.8-.1-.7-.6-1.2-1.4-1.4l-1.4-.4-1.1 2.8 1.2 2z"/></svg>`,
};

interface NativeVariant {
  label: string;
  heading: string;
  blurb: string;
  steps: string[];
  actions: { action: string; label: string; filled: boolean }[];
  note: string;
}

/** Per-OS content for the native-app install block. */
const NATIVE_VARIANTS: Record<NativeOs, NativeVariant> = {
  win: {
    label: 'Windows',
    heading: 'Native app for Windows',
    blurb: 'A desktop build with the full agent UI, parallel sessions, and a built-in terminal.',
    steps: [
      `<strong>Download the installer.</strong> Grab the <code>.exe</code> below and run it.`,
      `<strong>Run setup.</strong> Accept the license and pick a location — defaults land in <code>%LocalAppData%\\Codivus Code</code>.`,
      `<strong>Sign in.</strong> Codivus Code opens a browser to complete OAuth and returns a device-scoped token.`,
      `<strong>Open a project.</strong> Drag any repo folder onto the app or use <em>File → Open Folder</em>.`,
    ],
    actions: [{ action: 'download-win', label: 'Download for Windows', filled: true }],
    note: `Windows 10 and 11, 64-bit. An MSI build is available for enterprise deployments — <a href="/enterprise" data-public-path="/enterprise">ask the team</a>.`,
  },
  linux: {
    label: 'Linux',
    heading: 'Native app for Linux',
    blurb: 'A .deb package for Debian, Ubuntu, and compatible distributions.',
    steps: [
      `<strong>Add the apt repository.</strong><pre class="install-code"><code>echo "deb [trusted=yes] https://apt.codivus.africa stable main" | sudo tee /etc/apt/sources.list.d/codivus.list</code></pre>`,
      `<strong>Install.</strong><pre class="install-code"><code>sudo apt update &amp;&amp; sudo apt install codivus</code></pre>Or download the <code>.deb</code> below and run:<pre class="install-code"><code>cd ~/Downloads &amp;&amp; sudo apt install ./codivus_*.deb</code></pre>Then add the repo above for future updates.`,
      `<strong>Sign in.</strong> Codivus opens your browser for OAuth and stores credentials in the system keyring.`,
      `<strong>Open a project.</strong> Start from the app launcher or run <code>codivus ~/work/repo</code>.`,
    ],
    actions: [
      { action: 'download-linux-deb', label: 'Download .deb', filled: true },
    ],
    note: `Requires glibc 2.31+ and a desktop session with keyring support. For managed Linux workstations, <a href="/enterprise" data-public-path="/enterprise">request repository access</a>.`,
  },
};

/** Render the native-app install block. */
function nativeVariantHtml(os: NativeOs): string {
  const v = NATIVE_VARIANTS[os];
  const actions = v.actions
    .map(a => `<a class="btn ${a.filled ? 'btn-filled' : 'btn-ghost'}" href="#" data-install-action="${a.action}">${a.label}</a>`)
    .join('\n      ');
  return `
  <div class="native-variant${os === 'win' ? ' active' : ''}" data-native-variant="${os}">
    <div class="install-block-head">
      <div>
        <h2>${v.heading}</h2>
        <p>${v.blurb}</p>
      </div>
    </div>
    <ol class="install-steps">
      ${v.steps.map(s => `<li>${s}</li>`).join('\n      ')}
    </ol>
    <div class="install-actions">
      ${actions}
      <button type="button" class="btn btn-ghost" data-install-guide="native">Read the install guide</button>
    </div>
    <p class="install-note">${v.note}</p>
  </div>`;
}

export function renderInstall(): string {
  const nativeIcon = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="4" width="18" height="12" rx="2"/><path d="M8 20h8"/><path d="M12 16v4"/></svg>`;
  const vscIcon = `<svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="#0066B8" d="M23.15 2.587L18.21.21a1.494 1.494 0 00-1.705.29l-9.46 8.63-4.12-3.128a.999.999 0 00-1.276.057L.327 7.261A1 1 0 00.326 8.74L3.899 12 .326 15.26a1 1 0 00.001 1.479L1.65 17.94a.999.999 0 001.276.057l4.12-3.128 9.46 8.63a1.492 1.492 0 001.704.29l4.942-2.377A1.5 1.5 0 0024 20.06V3.939a1.5 1.5 0 00-.85-1.352zm-5.146 14.861L10.826 12l7.178-5.448v10.896z"/></svg>`;
  const cliIcon = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg"><polyline points="4 17 10 11 4 5"/><line x1="12" y1="19" x2="20" y2="19"/></svg>`;
  const jbIcon  = `<svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><rect width="24" height="24" rx="3" fill="#000"/><text x="2.5" y="13.5" fill="#fff" font-family="Arial,Helvetica,sans-serif" font-size="9" font-weight="900" font-style="italic">IJ</text><line x1="11" y1="24" x2="24" y2="11" stroke="#FE315D" stroke-width="3" stroke-linecap="round"/><line x1="16" y1="24" x2="24" y2="16" stroke="#F97316" stroke-width="3" stroke-linecap="round"/><line x1="20" y1="24" x2="24" y2="20" stroke="#FFB100" stroke-width="3" stroke-linecap="round"/></svg>`;
  const inner = `
<section class="generic-section install-page">
  <header class="generic-header">
    <h1>Install Codivus Code</h1>
    <p>Pick the surface that fits how you work. All three connect to the same workspace, so sessions sync automatically.</p>
  </header>

  <div class="install-anchors" role="tablist">
    <button type="button" class="install-anchor active" data-install-tab="cli" role="tab" aria-selected="true">${cliIcon}<span>CLI</span></button>
    <button type="button" class="install-anchor" data-install-tab="native" role="tab" aria-selected="false">${nativeIcon}<span>Native app</span></button>
  </div>
  <p class="install-recommendation"><strong>Our recommendation:</strong> The CLI is our most actively maintained surface and works on every platform. As a small team, our native app support is limited — we strongly encourage starting with the CLI.</p>

  <div class="install-block" id="native">
    <div class="native-os-switch" role="tablist" aria-label="Native app platform">
      <button type="button" class="install-anchor active" data-native-os="win" role="tab" aria-selected="true">${OS_ICON.win}<span>Windows</span></button>
      <button type="button" class="install-anchor" data-native-os="linux" role="tab" aria-selected="false">${OS_ICON.linux}<span>Linux</span></button>
    </div>
    ${nativeVariantHtml('win')}
    ${nativeVariantHtml('linux')}
  </div>

  <div class="install-block" id="vscode">
    <div class="install-block-head">
      <span class="install-block-icon">${vscIcon}</span>
      <div>
        <h2>VS Code extension</h2>
        <p>Run the agent in the editor you already have open. Works with VS Code, Cursor, and VSCodium.</p>
      </div>
    </div>
    <ol class="install-steps">
      <li><strong>Open the Extensions panel.</strong> <kbd>Cmd/Ctrl + Shift + X</kbd>.</li>
      <li><strong>Search for "Codivus Code"</strong> and click <em>Install</em>.</li>
      <li><strong>Sign in</strong> from the Codivus Code sidebar — the editor will handle the OAuth handshake.</li>
      <li><strong>Start a session</strong> with <kbd>Cmd/Ctrl + Shift + C</kbd> or the Codivus Code icon in the activity bar.</li>
    </ol>
    <div class="install-actions">
      <a class="btn btn-filled" href="#" data-install-action="marketplace">Open in Marketplace</a>
      <a class="btn btn-ghost" href="#" data-install-action="vsix">Download .vsix</a>
    </div>
  </div>

  <div class="install-block" id="jetbrains">
    <div class="install-block-head">
      <span class="install-block-icon">${jbIcon}</span>
      <div>
        <h2>JetBrains plugin</h2>
        <p>Run the agent inside IntelliJ IDEA, PyCharm, WebStorm, GoLand, Rider, Android Studio, or any other JetBrains IDE.</p>
      </div>
    </div>
    <ol class="install-steps">
      <li><strong>Open Settings.</strong> <kbd>Cmd/Ctrl + ,</kbd> → <em>Plugins</em>.</li>
      <li><strong>Search for "Codivus Code"</strong> in the Marketplace tab and click <em>Install</em>.</li>
      <li><strong>Restart the IDE</strong> when prompted, then open the Codivus Code tool window from the right-hand activity bar.</li>
      <li><strong>Sign in</strong> via <em>Tools → Codivus Code → Sign In…</em> — the IDE handles the OAuth handshake.</li>
    </ol>
    <div class="install-actions">
      <a class="btn btn-filled" href="#" data-install-action="jb-marketplace">Open in JetBrains Marketplace</a>
      <a class="btn btn-ghost" href="#" data-install-action="jb-zip">Download .zip</a>
    </div>
    <p class="install-note">Requires a 2024.2+ JetBrains IDE. Side-loading: <em>Settings → Plugins → ⚙ → Install Plugin from Disk…</em>, then pick the <code>.zip</code>.</p>
  </div>

  <div class="install-block active" id="cli">
    <div class="install-block-head">
      <span class="install-block-icon">${cliIcon}</span>
      <div>
        <h2>Command-line (CLI)</h2>
        <p>Perfect for CI, remote dev containers, or engineers who live in a terminal.</p>
      </div>
    </div>
    <div class="install-tabs">
      <div class="install-tab-title">npm</div>
      <pre class="install-code"><code>npm install -g @codivus/cli</code></pre>
    </div>
    <ol class="install-steps">
      <li><strong>Verify the install.</strong> <code>codivus --version</code></li>
      <li><strong>Log in.</strong> <code>codivus login</code> — on headless machines pass <code>--token $CODIVUS_TOKEN</code> instead.</li>
      <li><strong>Run your first task.</strong> <code>codivus "add a /logout endpoint"</code> inside any repo.</li>
    </ol>
  </div>

  <div class="install-help">
    <a class="btn btn-filled" href="/docs" data-public-path="/docs">More documentation</a>
  </div>

  <div class="doc-modal" id="installGuideModal" aria-hidden="true" role="dialog" aria-modal="true">
    <div class="doc-modal-backdrop" data-install-guide-close></div>
    <div class="doc-modal-panel" role="document">
      <button class="doc-modal-close" data-install-guide-close aria-label="Close">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="6" y1="18" x2="18" y2="6"/></svg>
      </button>
      <div class="doc-modal-body" id="installGuideBody"></div>
    </div>
  </div>
</section>`;
  return publicShell(inner, '');
}

const INSTALL_GUIDES: Record<string, { title: string; body: string }> = {
  native: {
    title: 'Installing the native app on Windows and Linux',
    body: `
      <p>The Codivus Code native app gives you the full agent experience on Windows and Linux — parallel sessions, an embedded terminal, and offline-friendly settings.</p>
      <h3>System requirements</h3>
      <ul><li>Windows 10 (22H2), Windows 11, or a modern Linux desktop with glibc 2.31+</li><li>x64 or ARM64 CPU</li><li>4 GB RAM minimum, 8 GB recommended</li><li>500 MB free disk</li></ul>
      <h3>Installing with the <code>.exe</code></h3>
      <ol>
        <li>Download <code>Codivus Code-Setup-1.4.2.exe</code> from the Install page.</li>
        <li>Double-click the installer. Windows SmartScreen may warn the first time — choose <em>More info → Run anyway</em>.</li>
        <li>Accept the license and pick an install location. The default is <code>%LocalAppData%\\Codivus Code</code>.</li>
        <li>Leave <em>"Launch Codivus Code when Windows starts"</em> checked if you want it available at boot.</li>
        <li>Click <em>Install</em>. The installer completes in under 30 seconds.</li>
      </ol>
      <h3>Installing with MSI (enterprise)</h3>
      <pre><code>msiexec /i Codivus Code-1.4.2.msi /quiet INSTALLDIR="C:\\Program Files\\Codivus Code"</code></pre>
      <p>MSI is suitable for Intune, SCCM, and Group Policy deployments. A signed certificate chain is included.</p>
      <h3>Installing on Linux</h3>
      <ol>
        <li>Download the <code>.deb</code> package for Debian and Ubuntu.</li>
        <li>Run <code>sudo apt install ./Codivus-Code-*.deb</code>, or double-click the file in your file manager.</li>
        <li>Confirm your desktop keyring is unlocked so Codivus Code can store the device token securely.</li>
      </ol>
      <h3>First run</h3>
      <ol>
        <li>Launch Codivus Code from the Start menu.</li>
        <li>The app opens a browser for OAuth sign-in and returns a device-scoped token.</li>
        <li>Drag any repo folder onto the window, or use <em>File → Open Folder</em>.</li>
        <li>Hit <kbd>Ctrl + Shift + C</kbd> to start your first agent session.</li>
      </ol>
      <h3>Updating</h3>
      <p>Updates install silently in the background and apply on the next launch. You can force-check via <em>Help → Check for updates</em>.</p>
      <h3>Uninstalling</h3>
      <p>Remove Codivus Code from <em>Settings → Apps</em> or via <code>winget uninstall Codivus Code</code>. User data lives under <code>%AppData%\\Codivus Code</code> and can be removed manually.</p>
      <h3>Troubleshooting</h3>
      <ul>
        <li><strong>SmartScreen blocks the installer</strong> — the download is signed with an EV certificate; the warning disappears after enough successful installs, or use the MSI.</li>
        <li><strong>App won\u2019t sign in</strong> — check your firewall allows outbound HTTPS to <code>platform.api.codivus.africa</code>.</li>
        <li><strong>Agent can\u2019t run shell commands</strong> — allow the bundled sandbox binary in Windows Defender.</li>
      </ul>`,
  },
};

export function initInstall(): void {
  bindPublicNav();
  // Install-action buttons (Download for Windows, Download .vsix, …) start as
  // placeholders that toast "coming soon". A button whose artifact is actually
  // hosted at /downloads/ gets upgraded to a real download link once the
  // manifest loads — see scripts/collect-downloads.mjs + server.js.
  const actionButtons = document.querySelectorAll<HTMLAnchorElement>('[data-install-action]');
  actionButtons.forEach(a => {
    a.addEventListener('click', (e) => {
      if (a.dataset.installReady === '1') return;  // real file — let the download proceed
      e.preventDefault();
      toast('info', 'Download', 'Public download link will go live shortly.');
    });
  });

  // Maps each install-action button to its artifact key in the download
  // manifest (written by collect-downloads.mjs, served at /downloads/).
  const ARTIFACT_BY_ACTION: Record<string, string> = {
    'download-win': 'desktop-win',
    'download-linux-deb': 'desktop-linux-deb',
    'vsix': 'vscode',
  };
  const fmtBytes = (n: number): string => {
    if (n >= 2 ** 30) return `${(n / 2 ** 30).toFixed(1)} GB`;
    if (n >= 2 ** 20) return `${(n / 2 ** 20).toFixed(1)} MB`;
    if (n >= 2 ** 10) return `${(n / 2 ** 10).toFixed(0)} KB`;
    return `${n} B`;
  };
  type DownloadArtifact = { url: string; file: string; version?: string; size?: number };
  void fetch('/downloads/manifest.json', { cache: 'no-store' })
    .then(r => (r.ok ? r.json() : null))
    .then((manifest: { artifacts?: Record<string, DownloadArtifact> } | null) => {
      const artifacts = manifest?.artifacts;
      if (!artifacts) return;
      actionButtons.forEach(a => {
        const key = ARTIFACT_BY_ACTION[a.dataset.installAction ?? ''];
        const art = key ? artifacts[key] : undefined;
        if (!art?.url) return;
        a.href = art.url;
        a.setAttribute('download', art.file);
        a.dataset.installReady = '1';
        const meta: string[] = [];
        if (art.version) meta.push(`v${art.version}`);
        if (art.size) meta.push(fmtBytes(art.size));
        if (meta.length) a.title = meta.join(' · ');
      });
    })
    .catch(() => { /* no manifest yet — buttons keep the placeholder toast */ });

  const tabs = document.querySelectorAll<HTMLButtonElement>('[data-install-tab]');
  const blocks = document.querySelectorAll<HTMLElement>('.install-block');
  tabs.forEach(b => {
    b.addEventListener('click', () => {
      const id = b.dataset.installTab || '';
      tabs.forEach(t => {
        const on = t === b;
        t.classList.toggle('active', on);
        t.setAttribute('aria-selected', String(on));
      });
      blocks.forEach(bl => { bl.classList.toggle('active', bl.id === id); });
    });
  });

  const nativeOsButtons = document.querySelectorAll<HTMLButtonElement>('[data-native-os]');
  const nativeVariants = document.querySelectorAll<HTMLElement>('[data-native-variant]');
  nativeOsButtons.forEach(b => {
    b.addEventListener('click', () => {
      const os = b.dataset.nativeOs || '';
      nativeOsButtons.forEach(btn => {
        const on = btn === b;
        btn.classList.toggle('active', on);
        btn.setAttribute('aria-selected', String(on));
      });
      nativeVariants.forEach(v => { v.classList.toggle('active', v.dataset.nativeVariant === os); });
    });
  });

  const guideModal = document.getElementById('installGuideModal');
  const guideBody  = document.getElementById('installGuideBody');
  const openGuide = (key: string) => {
    const guide = INSTALL_GUIDES[key];
    if (!guide || !guideModal || !guideBody) return;
    guideBody.innerHTML = `
      <div class="doc-meta"><span class="doc-section">Install guide</span></div>
      <h1 class="doc-title">${escHtml(guide.title)}</h1>
      <div class="doc-content">${guide.body}</div>`;
    guideModal.classList.add('open');
    guideModal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  };
  const closeGuide = () => {
    if (!guideModal) return;
    guideModal.classList.remove('open');
    guideModal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  };
  document.querySelectorAll<HTMLButtonElement>('[data-install-guide]').forEach(b => {
    b.addEventListener('click', () => openGuide(b.dataset.installGuide || ''));
  });
  guideModal?.querySelectorAll<HTMLElement>('[data-install-guide-close]').forEach(el => {
    el.addEventListener('click', closeGuide);
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeGuide(); });

  // Copy buttons on all pre>code blocks in the install page
  document.querySelectorAll<HTMLElement>('.install-page pre').forEach(pre => {
    const code = pre.querySelector('code');
    if (!code) return;
    const btn = document.createElement('button');
    btn.className = 'copy-btn';
    btn.setAttribute('aria-label', 'Copy');
    btn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>`;
    pre.style.position = 'relative';
    pre.appendChild(btn);
    btn.addEventListener('click', () => {
      navigator.clipboard.writeText(code.innerText.trim()).then(() => {
        btn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--green)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>`;
        setTimeout(() => {
          btn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>`;
        }, 1800);
      });
    });
  });
}

export function initEnterprise(): void {
  bindPublicNav();
  document.getElementById('enterpriseForm')?.addEventListener('submit', (e) => {
    e.preventDefault();
    toast('success', 'Thanks!', 'We\u2019ll be in touch within one business day.');
  });
}

// ---- Legal (Privacy / Terms) ----
//
// Standard SaaS legal copy for a coding-agent product. Both pages carry a
// visible "Draft \u2014 pending legal review" banner until counsel signs off.

const LEGAL_DRAFT_NOTE = `
  <div class="legal-draft" role="note">
    <strong>Draft \u2014 pending legal review.</strong>
    This document is a working draft and has not yet been reviewed by counsel. It may change before it becomes binding.
  </div>`;

export function renderPrivacy(): string {
  const inner = `
<section class="generic-section">
  <header class="generic-header">
    <h1>Privacy Policy</h1>
    <p>How Codivus Code collects, uses, and protects your data.</p>
  </header>
  ${LEGAL_DRAFT_NOTE}
  <article class="legal-body">
    <p class="legal-updated">Last updated: July 5, 2026</p>

    <h2>1. Who we are</h2>
    <p>Codivus Code is a coding-agent service operated by Codivus Africa ("Codivus", "we", "us"). This policy describes what personal data we collect when you use the Codivus Code website, apps, CLI, and editor extensions (together, the "Service"), and how we handle it.</p>

    <h2>2. Data we collect</h2>
    <ul>
      <li><strong>Account data.</strong> Your email address and the profile details you provide when you create an account (such as a display name). We use this to authenticate you, secure your account, and contact you about the Service.</li>
      <li><strong>Billing data.</strong> Payments are processed by our payment providers. We receive confirmation of payment, your plan, and billing status \u2014 we do not store your full card or mobile-money details ourselves.</li>
      <li><strong>Code and prompts.</strong> The prompts you send and the code the agent reads or edits are processed to provide the Service \u2014 to run your sessions, produce responses, and maintain session history you can return to. We do not sell this content or use it for advertising.</li>
      <li><strong>Usage data.</strong> Session metadata such as credit consumption, feature usage, timestamps, and diagnostic logs, used for billing, abuse prevention, and improving reliability.</li>
    </ul>

    <h2>3. How we use your data</h2>
    <ul>
      <li>To provide, operate, and secure the Service.</li>
      <li>To meter usage and bill your subscription or credit packs.</li>
      <li>To communicate service, security, and billing notices.</li>
      <li>To detect abuse and enforce our Terms of Service.</li>
    </ul>

    <h2>4. Sharing</h2>
    <p>We share data only with the subprocessors needed to run the Service: model-inference providers (to generate agent responses to your prompts), payment providers (to process payments), and infrastructure providers (hosting, email delivery). We do not sell personal data.</p>

    <h2>5. Retention</h2>
    <p>Account data is kept while your account is active. Session content is retained so you can revisit your history; you can delete sessions and projects from your workspace. When you close your account we delete or anonymize your personal data within a reasonable period, except where the law requires longer retention (for example, billing records).</p>

    <h2>6. Security</h2>
    <p>Data is encrypted in transit, access is scoped by authentication tokens, and agent tool calls are logged for auditability. No system is perfectly secure \u2014 report concerns to the contact address below.</p>

    <h2>7. Your rights</h2>
    <p>You may request access to, correction of, or deletion of your personal data by contacting us. If you are in a jurisdiction with data-protection law (such as the Malawi Data Protection Act or the GDPR), you may have additional rights, including complaint to a supervisory authority.</p>

    <h2>8. Contact</h2>
    <p>Questions about this policy: <a href="/contact" data-public-path="/contact">contact us</a>.</p>
  </article>
</section>`;
  return publicShell(inner, '');
}

export function initPrivacy(): void { bindPublicNav(); }

export function renderTerms(): string {
  const inner = `
<section class="generic-section">
  <header class="generic-header">
    <h1>Terms of Service</h1>
    <p>The agreement that governs your use of Codivus Code.</p>
  </header>
  ${LEGAL_DRAFT_NOTE}
  <article class="legal-body">
    <p class="legal-updated">Last updated: July 5, 2026</p>

    <h2>1. The Service</h2>
    <p>Codivus Code is a coding agent that reads, edits, and runs code in your projects on your instruction. By creating an account or using the Service you agree to these terms with Codivus Africa ("Codivus").</p>

    <h2>2. Your account</h2>
    <p>You must provide accurate account information and keep your credentials secure. You are responsible for activity under your account and for ensuring your use complies with applicable law.</p>

    <h2>3. Plans, credits, and billing</h2>
    <ul>
      <li>Paid plans bill per seat per billing period, with a monthly credit allocation. Plan credits reset each period; purchased credit packs keep their balance until used or expired.</li>
      <li>Fees are charged through our payment providers in your selected currency. Prices may change with notice effective from your next billing period.</li>
      <li>You can upgrade, downgrade, or cancel from your workspace. Fees already paid are non-refundable except where the law requires otherwise.</li>
    </ul>

    <h2>4. Your content</h2>
    <p>You retain all rights to your code, prompts, and repositories. You grant Codivus the limited license needed to process that content to provide the Service \u2014 running agent sessions, generating output, and storing your session history. Output the agent produces in your projects is yours; you are responsible for reviewing it before you rely on or ship it.</p>

    <h2>5. Acceptable use</h2>
    <ul>
      <li>No unlawful use, including developing malware or infringing others' rights.</li>
      <li>No attempts to circumvent usage metering, rate limits, or plan restrictions.</li>
      <li>No reselling of the Service, sharing of accounts to evade seat limits, or abuse of free-tier allocations.</li>
      <li>No probing, disrupting, or reverse-engineering the Service.</li>
    </ul>

    <h2>6. Disclaimers</h2>
    <p>The Service is provided "as is". Agent output can be wrong; you must review changes before merging or deploying them. To the maximum extent permitted by law, Codivus disclaims implied warranties and is not liable for indirect or consequential damages. Codivus's total liability is capped at the amounts you paid for the Service in the twelve months before the claim.</p>

    <h2>7. Suspension and termination</h2>
    <p>We may suspend or terminate accounts that breach these terms or create risk for the Service or other users. You may stop using the Service and close your account at any time.</p>

    <h2>8. Changes</h2>
    <p>We may update these terms; material changes will be announced with reasonable notice. Continued use after a change takes effect constitutes acceptance.</p>

    <h2>9. Contact</h2>
    <p>Questions about these terms: <a href="/contact" data-public-path="/contact">contact us</a>.</p>
  </article>
</section>`;
  return publicShell(inner, '');
}

export function initTerms(): void { bindPublicNav(); }

// ---- Contact ----

export function renderContact(): string {
  const inner = `
<section class="generic-section">
  <header class="generic-header">
    <h1>Contact</h1>
    <p>Questions, feedback, or a data request \u2014 reach the team directly.</p>
  </header>
  <div class="contact-cards">
    <article class="feature-card">
      <span class="feature-icon">\u{2709}\u{FE0F}</span>
      <h3>Email us</h3>
      <p>Support, privacy, and general questions. We reply within a business day.</p>
      <a class="btn btn-filled" href="mailto:hello@codivus.africa">hello@codivus.africa</a>
    </article>
    <article class="feature-card">
      <span class="feature-icon">\u{1F4BC}</span>
      <h3>Talk to sales</h3>
      <p>Enterprise plans, VPC deployments, custom contracts, and procurement.</p>
      <a class="btn btn-ghost" href="/enterprise" data-public-path="/enterprise">Enterprise \u2192</a>
    </article>
  </div>
</section>`;
  return publicShell(inner, '');
}

export function initContact(): void { bindPublicNav(); }

